import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Card, Form, InputNumber, Button, Space, Row, Col, Divider, Typography,
  Alert, message, Select, Descriptions, Tag, Input, DatePicker, Radio, Spin, Table, Tooltip, Modal,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, ThunderboltOutlined, SafetyOutlined,
  MedicineBoxOutlined, ClockCircleOutlined, FileTextOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { adminAPI, doseAPI, intakeAPI, modelAPI, reportAPI, drugAPI } from '../services/api';
import { useResponsive } from '../contexts/ResponsiveContext';
import PKCurve from './PKCurve';
import { PkPdResultsCard } from './DoseCalculation';
import ClinicalInterpretation from './ClinicalInterpretation';
import EditableCandidateRegimenCard, {
  type CandidateRegimenRecalcState,
} from './mobile/EditableCandidateRegimenCard';
import GroupedCandidateRegimenTable, {
  resolveCandidateSourceIndex,
} from './GroupedCandidateRegimenTable';
import {
  buildTdmPoints,
  calcSimDoses,
  generateCombinedPKCurveMulti,
  generatePKCurve,
  normalizeDosingSegments,
} from '../utils/pkCurveGen';
import {
  countDosesAtSampling,
  countDosesBeforeSwitch,
  hoursBetween,
  SAMPLING_BOUNDARY_TOLERANCE_MIN,
} from '../utils/doseCount';
import { normalizePkpdResults } from '../utils/pkpdResults';
import { resolveEvaluationStatus, resolveRegimenStatus } from '../utils/regimenStatus';
import { METHOD_CONFIG, type RegimenColumn } from '../constants/methodConfig';
import { formatDoseMgKg } from '../utils/doseDisplayFormat';
import {
  canonicalizeTdmConcentrations,
  convertCanonicalValue,
  convertUnitValue,
  formatCanonicalDose,
  projectCanonicalConcentrationCurve,
  projectCanonicalTdmPoints,
  unitFromResult,
} from '../utils/quantityUnits';
import type { AdministrationMode, Covariates, DoseResult, ModelListItem } from '../types';
import {
  applyConfiguredConcentrationUnits,
  findTargetForAdministrationMode,
  getAdministrationModeLabel,
  getConfiguredInputUnit,
  getConcentrationDisplayUnit,
  getPreferredTargetParameter as getPreferredTargetParameterForMode,
  getSupportedAdministrationModes,
  resolveAvailableCalculationMethods,
} from '../utils/doseMethodVisibility';
import { formatDisplayNumber } from '../utils/displayNumbers';
import { buildOptimizeTargetFields } from '../utils/optimizeTargetFields';
import { getExposureSafetyLimit } from '../utils/safetyRules';
import {
  hasPosteriorPredictionBand,
  resolveCurrentRegimenCurve,
} from '../utils/tdmCurveContract';

const { Title, Text } = Typography;
const { TextArea } = Input;

const normalizeFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const PK_PARAMETER_KEYS = new Set([
  'CL', 'Cl', 'cl', 'clearance', 'clearance_l_h', 'CL_individual', 'CL_pop',
  'Vc', 'VC', 'vc', 'V', 'V1', 'Vd', 'vd', 'volume', 'Vc_individual', 'Vc_pop',
  'ke', 'Ke', 'kel', 'KEL', 'half_life',
]);

// Keep the clinician-facing label aligned with the short posologyr function
// name. The detailed pipeline identifier remains available in the raw result
// for diagnostics, but should not dominate the result panel.
const OPTIMIZATION_METHOD_LABELS: Record<string, string> = {
  posologyr_poso_dose_auc_configured_grid_rxsolve: 'poso_dose_auc',
  posologyr_poso_dose_auc_per_interval: 'poso_dose_auc',
  posologyr_poso_dose_conc_configured_grid_rxsolve: 'poso_dose_conc',
  posologyr_poso_dose_conc_per_interval: 'poso_dose_conc',
  posologyr_continuous_auc_maintenance: 'poso_dose_auc',
  posologyr_continuous_css_maintenance: 'poso_dose_conc',
};

const formatOptimizationMethodLabel = (method: unknown): string => {
  const normalized = String(method ?? '').trim();
  if (!normalized) return '';
  if (OPTIMIZATION_METHOD_LABELS[normalized]) return OPTIMIZATION_METHOD_LABELS[normalized];
  const posologyrFunction = normalized.match(/(?:^|_)poso_dose_(auc|conc)(?:_|$)/i);
  return posologyrFunction ? `poso_dose_${posologyrFunction[1].toLowerCase()}` : normalized;
};

const hasDisplayablePkParameters = (pk: unknown): pk is Record<string, unknown> => (
  !!pk
  && typeof pk === 'object'
  && Object.entries(pk as Record<string, unknown>).some(
    ([key, value]) => PK_PARAMETER_KEYS.has(key) && normalizeFiniteNumber(value) !== null,
  )
);

const normalizeCandidateRegimen = (regimen: any, selectedMethod?: string, targetAucMic?: number | null) => {
  const dose = regimen?.dose ?? regimen?.dose_mg ?? regimen?.recommended_dose_mg;
  const doseKg = regimen?.dose_kg ?? regimen?.dose_mg_kg ?? regimen?.recommended_dose_mg_kg;
  const interval = regimen?.interval ?? regimen?.interval_h ?? regimen?.dosing_interval_h;
  const infusionH = regimen?.infusion_h ?? regimen?.infusion_duration_h;
  const normalizedPkpd = normalizePkpdResults(regimen?.pkpd_results, selectedMethod);
  const aucMic = normalizedPkpd?.['AUC/MIC'];

  return {
    ...regimen,
    dose,
    dose_mg: regimen?.dose_mg ?? dose,
    dose_kg: doseKg,
    dose_mg_kg: regimen?.dose_mg_kg ?? doseKg,
    interval,
    interval_h: regimen?.interval_h ?? interval,
    infusion_h: infusionH,
    predicted_auc24: regimen?.predicted_auc24 ?? regimen?.auc24,
    auc24_mic: regimen?.auc24_mic ?? aucMic?.value ?? aucMic?.details?.auc24_mic,
    target_auc_mic: targetAucMic,
    pkpd_results: normalizedPkpd,
  };
};

const isModelBasedCombinedCurve = (curve: any): boolean => (
  !!curve
  && (
    curve.model_based === true
    || curve.modelBased === true
    || String(curve.simulation_source || curve.simulationSource || '').includes('rxSolve')
  )
);

const nearlyEqual = (left: unknown, right: unknown, tolerance = 0.001): boolean => {
  const a = normalizeFiniteNumber(left);
  const b = normalizeFiniteNumber(right);
  if (a == null || b == null) return false;
  return Math.abs(a - b) <= tolerance;
};

const regimenMatchesResultCurve = (
  regimen: any,
  result: any,
  hasZeroInputDuration: boolean,
): boolean => {
  if (!regimen || !result) return false;
  const regimenDose = regimen.dose ?? regimen.dose_mg ?? regimen.recommended_dose_mg;
  const regimenInterval = regimen.interval ?? regimen.interval_h ?? regimen.dosing_interval_h;
  const regimenInfusion = hasZeroInputDuration ? 0 : (regimen.infusion_h ?? regimen.infusion_duration_h);
  const resultInfusion = hasZeroInputDuration ? 0 : result.infusion_duration_h;
  return nearlyEqual(regimenDose, result.recommended_dose_mg)
    && nearlyEqual(regimenInterval, result.dosing_interval_h)
    && nearlyEqual(regimenInfusion ?? resultInfusion, resultInfusion);
};

const TDM_OPTIMIZE_STYLES = `
  .tdm-optimize__candidate-table .ant-table-content {
    overflow-x: auto !important;
  }

  .tdm-optimize__candidate-table .ant-table {
    min-width: 760px;
  }

  .tdm-optimize__candidate-table .ant-table-cell {
    padding: 7px 5px !important;
    font-size: 12px;
    line-height: 1.3;
    overflow-wrap: normal;
  }

  .tdm-optimize__candidate-table .ant-table-thead > tr > th {
    color: #5F6F82;
    font-size: 11px;
    font-weight: 700;
  }

  .tdm-optimize__candidate-table .ant-input-number-input {
    padding-inline: 6px;
    font-size: 12px;
  }

  .tdm-optimize__candidate-table .ant-table-row {
    cursor: pointer;
  }

  .tdm-optimize__candidate-table .candidate-evaluation-column {
    position: sticky !important;
    right: 0;
    z-index: 2;
    background: #FFFFFF;
    box-shadow: -7px 0 9px -9px rgba(11, 37, 69, 0.7);
  }

  .tdm-optimize__candidate-table .candidate-evaluation-column .ant-tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    max-width: none;
    white-space: nowrap;
    line-height: 18px;
    margin: 0;
  }

  .tdm-optimize__candidate-table .ant-table-thead > tr > th.candidate-evaluation-column {
    z-index: 3;
    background: #FAFAFA;
  }

  .tdm-optimize__candidate-table .ant-table-tbody > tr:hover > td.candidate-evaluation-column {
    background: #FAFAFA !important;
  }

  .tdm-optimize__candidate-table .ant-table-tbody > tr.ant-table-row-selected > td.candidate-evaluation-column {
    background: #E6F4FF !important;
  }

  @media (max-width: 575px) {
    .tdm-optimize__candidate-table .ant-table-cell {
      padding: 6px 3px !important;
      font-size: 11px;
      vertical-align: top;
    }

    .tdm-optimize__candidate-table .ant-table {
      min-width: 760px;
    }

    .tdm-optimize__candidate-table .ant-tag {
      max-width: none;
      padding-inline: 4px;
      white-space: nowrap;
      text-align: center;
      line-height: 18px;
    }
  }

  .tdm-optimize__detail-card.ant-card {
    border-radius: 20px;
    border: 1px solid #DCE5EF;
    box-shadow: 0 12px 28px rgba(11, 37, 69, 0.08);
  }

  .tdm-optimize__detail-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 14px;
  }

  .tdm-optimize__detail-label {
    display: block;
    margin-bottom: 4px;
    color: #7A869A;
    font-size: 12px;
    line-height: 1.2;
  }

  .tdm-optimize__detail-value {
    color: #061529;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.2;
    word-break: break-word;
  }

  .tdm-optimize__detail-status {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
  }

  .tdm-optimize__detail-status-text {
    color: #7A869A;
    font-size: 12px;
    line-height: 1.45;
  }

  .tdm-optimize__result-grid {
    align-items: stretch;
  }

  .tdm-optimize__result-grid > .ant-col {
    display: flex;
  }

  .tdm-optimize__result-card.ant-card,
  .tdm-optimize__clinical-stack {
    width: 100%;
  }

  .tdm-optimize__result-card.ant-card {
    height: 100%;
  }

  .tdm-optimize__clinical-stack {
    display: flex;
    flex-direction: column;
    gap: 12px;
    height: 100%;
  }

  .tdm-optimize__clinical-card.ant-card {
    border-radius: 12px;
    border-color: #DCE5EF;
    box-shadow: none;
  }

  .tdm-optimize__clinical-card.ant-card .ant-card-head {
    min-height: 40px;
    padding: 0 14px;
  }

  .tdm-optimize__clinical-card.ant-card .ant-card-head-title {
    padding: 10px 0;
    font-size: 14px;
    font-weight: 700;
  }

  .tdm-optimize__clinical-card.ant-card .ant-card-body {
    padding: 12px 14px;
  }

  .tdm-optimize__safety-card.ant-card {
    flex: 0 0 auto;
  }

  .tdm-optimize__pk-card.ant-card {
    flex: 1 1 auto;
    min-height: 0;
  }

  .tdm-optimize__safety-status {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    min-height: 42px;
    padding: 8px 10px;
    border-radius: 10px;
    border: 1px solid #D7E3F1;
    background: #F7FAFD;
  }

  .tdm-optimize__safety-status--ok {
    border-color: #BFE8CF;
    background: #F3FBF6;
  }

  .tdm-optimize__safety-status--warning {
    border-color: #FFE1A8;
    background: #FFF9E8;
  }

  .tdm-optimize__safety-status--danger {
    border-color: #FFD1D1;
    background: #FFF5F5;
  }

  .tdm-optimize__safety-status .ant-tag {
    margin-inline-end: 0;
    font-weight: 700;
  }

  .tdm-optimize__safety-copy {
    color: #5C6B7A;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.35;
    white-space: nowrap;
  }

  .tdm-optimize__safety-alerts {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 92px;
    margin-top: 8px;
    overflow: auto;
  }

  .tdm-optimize__safety-alerts .ant-alert {
    margin-bottom: 0 !important;
    padding: 6px 10px;
    border-radius: 8px;
  }

  .tdm-optimize__pk-card .ant-descriptions-item {
    padding-bottom: 6px;
  }

  .tdm-optimize__sticky-cta {
    position: fixed;
    left: 16px;
    right: 16px;
    bottom: calc(88px + env(safe-area-inset-bottom));
    z-index: 26;
    padding: 12px;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.96);
    backdrop-filter: blur(12px);
    box-shadow: 0 18px 40px rgba(11, 37, 69, 0.16);
  }

  .tdm-optimize__sticky-spacer {
    height: 96px;
  }
`;

interface TDMOptimizePanelProps {
  caseId: string;
  covariates: Covariates;
  /** 上一步匹配选中的模型 ID，自动填入模型选择 */
  lastModelId?: string;
  /** 目标药物 ID，用于过滤模型和加载计算方法 */
  drugName?: string;
  administrationMode?: AdministrationMode;
  replayDraft?: {
    source?: string;
    sourceLabel?: string;
    calculationId?: string;
    modelId?: string;
    selectedMethod?: string;
    administrationMode?: AdministrationMode;
    futureInfusionDurationH?: number;
    dosingRegimens?: Array<Record<string, any>>;
    tdmSamples?: Array<Record<string, any>>;
    targetValues?: Record<string, number | undefined>;
    tdmValuesAreDisplayUnits?: boolean;
  } | null;
  /** 点击“生成报告”时回调，将结果传回父组件进入报告预览步骤 */
  onCalculated?: (result: DoseResult) => void;
}

/** Use the model's name column from model management */
const shortModelName = (m: ModelListItem): string => m.name;

const getAnchorRegimenStart = (regimens: any[] = []) => {
  const validStarts = regimens
    .map((reg) => (reg?.dosing_start_time ? dayjs(reg.dosing_start_time) : null))
    .filter((value): value is dayjs.Dayjs => !!value && value.isValid())
    .sort((a, b) => a.valueOf() - b.valueOf());
  return validStarts[0] || null;
};

const formatDateTimeToMinuteString = (value: unknown): string | undefined => {
  if (!value) return undefined;
  const parsed = dayjs(value as dayjs.ConfigType);
  if (!parsed.isValid()) return undefined;
  return parsed.format('YYYY-MM-DD HH:mm');
};

const serializeDosingRegimens = (regimens: any[] = []) => regimens.map((reg) => ({
  ...reg,
  dosing_start_time: formatDateTimeToMinuteString(reg?.dosing_start_time),
  n_doses_source: reg?.n_doses_source === 'manual' ? 'manual' : 'auto',
  n_doses_manual: reg?.n_doses_source === 'manual',
}));

const TDMOptimizePanel: React.FC<TDMOptimizePanelProps> = ({ caseId, covariates, lastModelId, drugName, administrationMode = 'intermittent', replayDraft, onCalculated }) => {
  const { isMobileShell } = useResponsive();
  const [form] = Form.useForm();
  const [models, setModels] = useState<ModelListItem[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DoseResult | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [selectedRegimen, setSelectedRegimen] = useState<number | null>(null);
  // 自动计算的各方案给药剂次
  const [autoRegimenDoses, setAutoRegimenDoses] = useState<Record<number, number | null>>({});
  const autoRegimenDosesRef = useRef<Record<number, number | null>>({});
  const isAutoUpdating = useRef(false);
  const replayDraftSignatureRef = useRef<string | null>(null);
  // 保存提交给后端的 TDM 数据，用于在药时曲线上绘制实测浓度点
  const [submittedTdmData, setSubmittedTdmData] = useState<any[] | null>(null);
  // 保存提交时的给药方案列表，用于重建药时曲线历史段
  const [submittedRegimens, setSubmittedRegimens] = useState<any[] | null>(null);
  const [submittedModelId, setSubmittedModelId] = useState<string | null>(null);
  const [submittedTargetAucMic, setSubmittedTargetAucMic] = useState<number | null>(null);
  const [modifiedRegimenDoses, setModifiedRegimenDoses] = useState<Record<number, number>>({});
  const [modifiedRegimenInfusions, setModifiedRegimenInfusions] = useState<Record<number, number>>({});
  const [regimenRecalcOverrides, setRegimenRecalcOverrides] = useState<Record<number, any>>({});
  const [regimenRecalcStatus, setRegimenRecalcStatus] = useState<Record<number, 'loading' | 'done' | 'error'>>({});
  const regimenDebounceRefs = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const regimenLatestRequestRef = useRef<Record<number, string>>({});
  const futureInfusionTouchedRef = useRef(false);
  const futureMicTouchedRef = useRef(false);

  // 药物配置 + 计算方法选择
  const [drugConfig, setDrugConfig] = useState<any>(null);
  const [selectedMethod, setSelectedMethod] = useState<string>('');
  const [tdmAdministrationMode, setTdmAdministrationMode] = useState<AdministrationMode>(administrationMode);
  const [administrationModeDirty, setAdministrationModeDirty] = useState(false);
  const supportedAdministrationModes = useMemo(() => {
    return getSupportedAdministrationModes(drugConfig);
  }, [drugConfig]);
  const isContinuousMode = tdmAdministrationMode === 'continuous';
  const isOralMode = tdmAdministrationMode === 'oral';
  const isBolusMode = tdmAdministrationMode === 'bolus';
  const hasZeroInputDuration = isOralMode || isBolusMode;
  const shortestCandidateIntervalH = useMemo(() => {
    const intervals = Array.isArray(drugConfig?.candidate_intervals_h)
      ? drugConfig.candidate_intervals_h
          .map((value: unknown) => normalizeFiniteNumber(value))
          .filter((value: number | null): value is number => value != null && value > 0)
      : [];
    return intervals.length > 0 ? Math.min(...intervals) : undefined;
  }, [drugConfig?.candidate_intervals_h]);
  const continuousTargetParameter = drugConfig?.continuous_infusion?.target_parameter || 'Css';
  const configuredConcentrationUnit = getConcentrationDisplayUnit(
    drugConfig,
    selectedMethod,
    tdmAdministrationMode,
    continuousTargetParameter,
  );
  const concentrationUnit = unitFromResult(result, 'concentration', configuredConcentrationUnit);
  const aucUnit = unitFromResult(result, 'auc', 'mg*h/L');
  const micUnit = unitFromResult(result, 'mic', drugConfig?.units?.mic?.display_unit || concentrationUnit);
  const doseMassUnit = unitFromResult(result, 'dose', 'mg');
  const formatResultDose = (value: unknown) => {
    const numeric = normalizeFiniteNumber(value);
    return numeric == null ? 'N/A' : formatCanonicalDose(numeric, { dose_mass_unit: doseMassUnit });
  };
  const formatResultDosePerKg = (value: unknown) => {
    const numeric = normalizeFiniteNumber(value);
    if (numeric == null) return 'N/A';
    const converted = convertUnitValue(numeric, 'mg/kg', `${doseMassUnit}/kg`, 'dose_per_weight');
    return `${formatDoseMgKg(converted)} ${doseMassUnit}/kg`;
  };
  const formatConcentrationValue = (value: unknown, decimals = 2) => {
    const numberValue = convertCanonicalValue(value, concentrationUnit, 'concentration');
    return numberValue != null ? `${formatDisplayNumber(numberValue, decimals)} ${concentrationUnit}` : 'N/A';
  };
  const formatAucValue = (value: unknown, decimals = 1) => {
    const numberValue = convertCanonicalValue(value, aucUnit, 'auc');
    return numberValue != null ? `${formatDisplayNumber(numberValue, decimals)} ${aucUnit}` : 'N/A';
  };
  const getPreferredTargetParameter = (method: string): string | undefined => {
    return getPreferredTargetParameterForMode(method, tdmAdministrationMode, continuousTargetParameter);
  };
  const findTarget = (method: string, parameter = getPreferredTargetParameter(method)) => {
    return findTargetForAdministrationMode(
      drugConfig,
      method,
      tdmAdministrationMode,
      continuousTargetParameter,
      parameter,
    );
  };

  useEffect(() => () => {
    Object.values(regimenDebounceRefs.current).forEach((timer) => clearTimeout(timer));
  }, []);

  useEffect(() => {
    futureInfusionTouchedRef.current = false;
    futureMicTouchedRef.current = false;
    form.setFieldValue('future_infusion_duration_h', undefined);
    form.setFieldValue('mic', undefined);
    setAdministrationModeDirty(false);
    setTdmAdministrationMode(administrationMode);
  }, [administrationMode, drugName, form]);

  useEffect(() => {
    if (!administrationModeDirty) {
      setTdmAdministrationMode(administrationMode);
    }
  }, [administrationMode, administrationModeDirty]);

  useEffect(() => {
    if (supportedAdministrationModes.includes(tdmAdministrationMode)) return;
    const fallbackMode = supportedAdministrationModes.includes(administrationMode)
      ? administrationMode
      : (supportedAdministrationModes[0] || 'intermittent');
    setAdministrationModeDirty(false);
    setTdmAdministrationMode(fallbackMode);
  }, [administrationMode, supportedAdministrationModes, tdmAdministrationMode]);

  useEffect(() => {
    if (drugName) {
      drugAPI.get(drugName).then((config: any) => {
        setDrugConfig(config);
        const nextContinuousTargetParameter = config?.continuous_infusion?.target_parameter || 'Css';
        const replayMode = replayDraft?.administrationMode;
        const configSupportedModes = getSupportedAdministrationModes(config);
        const effectiveMode = replayMode && configSupportedModes.includes(replayMode)
          ? replayMode
          : tdmAdministrationMode;
        if (replayMode && configSupportedModes.includes(replayMode)) {
          setAdministrationModeDirty(true);
          setTdmAdministrationMode(replayMode);
        }
        const methods = resolveAvailableCalculationMethods(
          config,
          effectiveMode,
          nextContinuousTargetParameter,
        );
        const replayMethod = replayDraft?.selectedMethod;
        const nextMethod = replayMethod && methods.includes(replayMethod) ? replayMethod : (methods[0] || '');
        setSelectedMethod(nextMethod);
        const resolveTarget = (method: string, parameter?: string) => findTargetForAdministrationMode(
          config,
          method,
          effectiveMode,
          nextContinuousTargetParameter,
          parameter,
        );
        const inputs = METHOD_CONFIG[nextMethod]?.inputs || [];
        const currentValues = form.getFieldsValue();
        const defaults: Record<string, number> = {};
        for (const input of inputs) {
          if (currentValues[input.name] != null) continue; // 已有值则保留
          if (input.name === 'mic') {
            const configuredMic = normalizeFiniteNumber(config?.default_mic);
            if (configuredMic != null && configuredMic > 0) {
              defaults.mic = configuredMic;
            }
            continue;
          }
          const t = input.name === 'target_auc'
            ? resolveTarget('Target AUC', 'AUC24')
            : input.name === 'target_cmin'
              ? resolveTarget('Target Concentration', effectiveMode === 'continuous' ? nextContinuousTargetParameter : 'Cmin')
              : input.name === 'target_cmax'
                ? resolveTarget('Target Cmax', 'Cmax')
                : input.name === 'target_auc_mic'
                  ? resolveTarget('AUC/MIC', 'AUC24/MIC')
                  : input.name === 'target_ft'
                    ? resolveTarget('%fT>MIC', '%fT>MIC')
                    : undefined;
          if (t?.min != null) {
            defaults[input.name] = Math.round(((t.min) + (t.max || t.min)) / 2);
          }
        }
        if (Object.keys(defaults).length > 0) {
          form.setFieldsValue(defaults);
        }
      }).catch((error: any) => {
        setDrugConfig(null);
        message.error(error?.response?.data?.detail || '药物后台配置加载失败，无法执行 TDM 优化');
      });
    }
  }, [drugName, isContinuousMode, tdmAdministrationMode, form, replayDraft?.administrationMode, replayDraft?.selectedMethod]);

  useEffect(() => {
    loadModels();
  }, [drugName, tdmAdministrationMode]);

  const availableMethods = useMemo(() => {
    return resolveAvailableCalculationMethods(
      drugConfig,
      tdmAdministrationMode,
      continuousTargetParameter,
    );
  }, [drugConfig, continuousTargetParameter, tdmAdministrationMode]);

  useEffect(() => {
    if (availableMethods.length === 0) return;
    if (!availableMethods.includes(selectedMethod)) {
      setSelectedMethod(availableMethods[0]);
    }
  }, [availableMethods, selectedMethod]);

  // 当模型列表加载完成且存在 lastModelId 时，自动设置模型选择
  useEffect(() => {
    if (lastModelId && models.length > 0) {
      const exists = models.some((m) => m.model_id === lastModelId);
      if (exists) {
        form.setFieldValue('model_id', lastModelId);
      } else if (form.getFieldValue('model_id') === lastModelId) {
        form.setFieldValue('model_id', undefined);
      }
    }
  }, [form, lastModelId, models]);

  const loadModels = async () => {
    setModelsLoading(true);
    try {
      const data = await modelAPI.list(drugName, tdmAdministrationMode);
      setModels(data.models || []);
    } catch {
      setModels([]);
    } finally {
      setModelsLoading(false);
    }
  };

  /** Only show active models in the dropdown */
  const activeModels = useMemo(
    () => models.filter((m) => m.model_status === 'active'),
    [models],
  );

  /** 根据开始给药时间、采样时间、给药间隔，自动计算 `time_after_dose` 和 `n_doses` */
  const recalcAutoFields = (allValues?: any) => {
    if (isAutoUpdating.current) return;
    const values = allValues || form.getFieldsValue();
    const regimens: any[] = values.dosing_regimens || [];
    const samples: any[] = values.tdm_samples || [];

    // ---- 1. 自动计算每个方案的给药剂次 ----
    const newRegimenDoses: Record<number, number | null> = {};

    // 找到最晚的 TDM 采样时间
    let latestSampling: dayjs.Dayjs | null = null;
    samples.forEach((s: any) => {
      if (s?.sampling_time) {
        const t = dayjs(s.sampling_time);
        if (!latestSampling || t.isAfter(latestSampling)) {
          latestSampling = t;
        }
      }
    });

    regimens.forEach((_: any, idx: number) => {
      newRegimenDoses[idx] = null;
    });

    const sortedRegimens = regimens
      .map((reg: any, idx: number) => ({
        ...reg,
        originalIndex: idx,
        startAt: reg?.dosing_start_time ? dayjs(reg.dosing_start_time) : null,
      }))
      .filter((reg: any) => reg.startAt && reg.startAt.isValid())
      .sort((a: any, b: any) => a.startAt.valueOf() - b.startAt.valueOf());

    sortedRegimens.forEach((reg: any, timelineIdx: number) => {
      const idx = reg.originalIndex;
      const thisStart = reg.startAt;
      const interval = normalizeFiniteNumber(reg.dosing_interval);
      if (interval == null || interval <= 0) return;

      if (timelineIdx < sortedRegimens.length - 1) {
        // 非最后一个方案：根据下一方案开始时间和给药间隔向下取整后计算剂次
        const nextReg = sortedRegimens[timelineIdx + 1];
        newRegimenDoses[idx] = countDosesBeforeSwitch(thisStart, nextReg.startAt, interval);
      } else if (latestSampling) {
        // 最后一个方案（或唯一方案）：根据最晚 TDM 时间和给药间隔向下取整后计算剂次
        newRegimenDoses[idx] = countDosesAtSampling(thisStart, latestSampling, interval);
      }
    });

    setAutoRegimenDoses(newRegimenDoses);
    autoRegimenDosesRef.current = newRegimenDoses;

    // auto 模式始终跟随时间轴刷新；manual 模式保留用户覆盖值
    const currentRegimens = form.getFieldValue('dosing_regimens') || [];
    let changed = false;
    const updatedRegimens = currentRegimens.map((r: any, idx: number) => {
      const source = r?.n_doses_source === 'manual' ? 'manual' : 'auto';
      const nextAuto = newRegimenDoses[idx];
      if (source === 'manual') {
        if (r?.n_doses_source !== 'manual') {
          changed = true;
          return { ...r, n_doses_source: 'manual' };
        }
        return r;
      }
      if ((r?.n_doses ?? null) !== (nextAuto ?? null) || r?.n_doses_source !== 'auto') {
        changed = true;
        return { ...r, n_doses: nextAuto ?? undefined, n_doses_source: 'auto' };
      }
      return r;
    });
    if (changed) {
      isAutoUpdating.current = true;
      form.setFieldValue('dosing_regimens', updatedRegimens);
      isAutoUpdating.current = false;
    }
  };

  useEffect(() => {
    if (!replayDraft || !drugConfig) return;
    const signature = JSON.stringify(replayDraft);
    if (replayDraftSignatureRef.current === signature) return;
    replayDraftSignatureRef.current = signature;

    if (replayDraft.administrationMode && supportedAdministrationModes.includes(replayDraft.administrationMode)) {
      setAdministrationModeDirty(true);
      setTdmAdministrationMode(replayDraft.administrationMode);
    }
    if (replayDraft.selectedMethod) {
      setSelectedMethod(replayDraft.selectedMethod);
    }

    const dosingRegimens = (replayDraft.dosingRegimens || [])
      .filter((regimen) => regimen && (regimen.dose != null || regimen.dosing_start_time || regimen.dosing_interval != null))
      .map((regimen) => ({
        ...regimen,
        dose: regimen.dose == null
          ? regimen.dose
          : convertCanonicalValue(regimen.dose, doseMassUnit, 'mass'),
        dosing_start_time: regimen.dosing_start_time ? dayjs(regimen.dosing_start_time) : undefined,
        n_doses_source: regimen.n_doses_source === 'manual' ? 'manual' : 'auto',
      }));
    const tdmSamples = (replayDraft.tdmSamples || [])
      .filter((sample) => sample && (sample.concentration != null || sample.sampling_time))
      .map((sample) => ({
        ...sample,
        concentration: sample.concentration == null
          ? sample.concentration
          : replayDraft.tdmValuesAreDisplayUnits
            ? sample.concentration
            : convertCanonicalValue(sample.concentration, concentrationUnit, 'concentration'),
        sampling_time: sample.sampling_time ? dayjs(sample.sampling_time) : undefined,
      }));

    const nextValues = {
      ...form.getFieldsValue(true),
      ...(replayDraft.modelId ? { model_id: replayDraft.modelId } : {}),
      ...(replayDraft.futureInfusionDurationH != null
        ? { future_infusion_duration_h: replayDraft.futureInfusionDurationH }
        : {}),
      ...(replayDraft.targetValues || {}),
      ...(dosingRegimens.length ? { dosing_regimens: dosingRegimens } : {}),
      ...(tdmSamples.length ? { tdm_samples: tdmSamples } : {}),
    };
    form.setFieldsValue(nextValues);
    if (replayDraft.futureInfusionDurationH != null) {
      futureInfusionTouchedRef.current = true;
    }
    if (Object.prototype.hasOwnProperty.call(replayDraft.targetValues || {}, 'mic')) {
      futureMicTouchedRef.current = true;
    }
    recalcAutoFields(nextValues);
    setResult(null);
    setSelectedRegimen(null);
    message.success(`已加载${replayDraft.sourceLabel || '结构化详情'}，可直接复现优化`);
  }, [concentrationUnit, doseMassUnit, form, replayDraft, supportedAdministrationModes]);

  const handleFormValuesChange = (changedValues: any, allValues: any) => {
    if (isAutoUpdating.current) return;

    if (Object.prototype.hasOwnProperty.call(changedValues || {}, 'future_infusion_duration_h')) {
      futureInfusionTouchedRef.current = true;
    }
    if (Object.prototype.hasOwnProperty.call(changedValues || {}, 'mic')) {
      futureMicTouchedRef.current = true;
    }

    const changedRegimens = changedValues?.dosing_regimens;
    if (Array.isArray(changedRegimens)) {
      const currentRegimens = [...(allValues?.dosing_regimens || [])];
      let changed = false;

      changedRegimens.forEach((patch: any, idx: number) => {
        if (!patch || !currentRegimens[idx] || !Object.prototype.hasOwnProperty.call(patch, 'n_doses')) {
          return;
        }

        if (patch.n_doses == null || patch.n_doses === undefined) {
          if (currentRegimens[idx]?.n_doses_source !== 'auto') {
            currentRegimens[idx] = {
              ...currentRegimens[idx],
              n_doses: undefined,
              n_doses_source: 'auto',
            };
            changed = true;
          }
          return;
        }

        const nextSource = 'manual';
        if (currentRegimens[idx]?.n_doses_source !== nextSource) {
          currentRegimens[idx] = {
            ...currentRegimens[idx],
            n_doses_source: nextSource,
          };
          changed = true;
        }
      });

      if (changed) {
        isAutoUpdating.current = true;
        form.setFieldValue('dosing_regimens', currentRegimens);
        isAutoUpdating.current = false;
        recalcAutoFields({ ...allValues, dosing_regimens: currentRegimens });
        return;
      }
    }

    recalcAutoFields(allValues);
  };

  const handleOptimize = async (values: any) => {
    const {
      model_id,
      dosing_regimens,
      tdm_samples,
      future_infusion_duration_h: futureInfusionDurationH,
    } = values;

    if (!drugConfig) {
      message.error('药物后台配置尚未加载，无法执行优化');
      return;
    }
    if (!selectedMethod || !availableMethods.includes(selectedMethod)) {
      message.error('请选择后台已配置的计算方法');
      return;
    }

    if (drugName && model_id) {
      try {
        const confirmedContext = await intakeAPI.context({
          drug_id: drugName,
          covariates,
          confirmed_model_id: model_id,
          administration_mode: tdmAdministrationMode,
        });
        const readiness = confirmedContext.model_readiness?.find((item) => item.model_id === model_id);
        const missingForModel = readiness?.missing_required_covariates
          || confirmedContext.next_required_covariates.filter(
            (item) => item.requirement_level === 'required_for_selected_model'
              || (!item.requirement_level && item.required && (item.model_ids || []).includes(model_id)),
          );
        if (missingForModel.length > 0) {
          message.error(
            `TDM 所选模型还缺少必填协变量：${missingForModel.map((item) => item.label).join('、')}。请补齐后再优化。`,
          );
          return;
        }
      } catch (err: any) {
        message.error(err?.response?.data?.detail || 'TDM 模型必填协变量校验失败');
        return;
      }
    }

    if (!tdm_samples || tdm_samples.length === 0) {
      message.warning('请至少添加一条 TDM 血药浓度记录');
      return;
    }

    if (!dosing_regimens || dosing_regimens.length === 0) {
      message.warning('请填写当前给药方案');
      return;
    }

    const resolvedFutureInfusion = normalizeFiniteNumber(futureInfusionDurationH);
    if (!isContinuousMode && !hasZeroInputDuration && (resolvedFutureInfusion == null || resolvedFutureInfusion <= 0)) {
      message.error('请填写有效的输注时间');
      return;
    }

    const invalidRegimenIndex = dosing_regimens.findIndex((regimen: any) => {
      const dose = normalizeFiniteNumber(regimen?.dose);
      const interval = normalizeFiniteNumber(regimen?.dosing_interval);
      const infusion = normalizeFiniteNumber(regimen?.infusion_duration);
      const start = regimen?.dosing_start_time ? dayjs(regimen.dosing_start_time) : null;
      return dose == null || dose <= 0
        || interval == null || interval <= 0
        || !start?.isValid()
        || (!hasZeroInputDuration && (infusion == null || infusion <= 0));
    });
    if (invalidRegimenIndex >= 0) {
      message.error(`给药方案 #${invalidRegimenIndex + 1} 的开始时间、剂量、间隔或输注时间不完整`);
      return;
    }
    const firstRegimen = dosing_regimens[0];

    const normalizedRegimens = (dosing_regimens || []).map((reg: any, idx: number) => {
      const source = reg?.n_doses_source === 'manual' ? 'manual' : 'auto';
      return {
        ...reg,
        dose: reg?.dose == null
          ? reg?.dose
          : convertUnitValue(reg.dose, doseMassUnit, 'mg', 'mass'),
        infusion_duration: isContinuousMode ? 24 : (hasZeroInputDuration ? 0 : reg?.infusion_duration),
        n_doses: source === 'manual'
          ? reg?.n_doses
          : autoRegimenDosesRef.current[idx] ?? reg?.n_doses,
        n_doses_source: source,
      };
    });
    const serializedRegimens = serializeDosingRegimens(normalizedRegimens);
    const firstRegimenStart = getAnchorRegimenStart(normalizedRegimens) || dayjs(firstRegimen.dosing_start_time);
    const regimensByStartDesc = normalizedRegimens
      .map((reg: any, index: number) => ({
        ...reg,
        originalIndex: index,
        startAt: reg?.dosing_start_time ? dayjs(reg.dosing_start_time) : null,
      }))
      .filter((reg: any) => reg.startAt && reg.startAt.isValid())
      .sort((a: any, b: any) => b.startAt.valueOf() - a.startAt.valueOf());

    // For each TDM sample, find the applicable regimen based on timing
    const processedTdmData = tdm_samples.map((item: any) => {
      let timeAfterDose = 0;
      let nDoses = 1;
      let applicableRegimen = normalizedRegimens[normalizedRegimens.length - 1];
      let applicableIndex = normalizedRegimens.length - 1;
      let absoluteTime = 0;
      const samplingTimeText = formatDateTimeToMinuteString(item.sampling_time);

      if (samplingTimeText) {
        const samplingTime = dayjs(samplingTimeText);
        absoluteTime = +hoursBetween(firstRegimenStart, samplingTime).toFixed(2);

        for (const regimenEntry of regimensByStartDesc) {
          if (!regimenEntry.startAt.isAfter(samplingTime)) {
            applicableRegimen = regimenEntry;
            applicableIndex = regimenEntry.originalIndex;
            break;
          }
        }

        const regimenStart = dayjs(applicableRegimen.dosing_start_time || firstRegimen.dosing_start_time);
        const interval = Number(applicableRegimen.dosing_interval);
        const totalHours = hoursBetween(regimenStart, samplingTime);
        nDoses = countDosesAtSampling(regimenStart, samplingTime, interval);
        const lastDoseHours = (nDoses - 1) * interval;
        timeAfterDose = +(totalHours - lastDoseHours).toFixed(2);
      }

      return {
        concentration: item.concentration,
        sampling_time: samplingTimeText,
        sample_time: samplingTimeText,
        time_after_dose: timeAfterDose,
        dose: applicableRegimen.dose,
        administration_mode: tdmAdministrationMode,
        infusion_duration: hasZeroInputDuration ? 0 : applicableRegimen.infusion_duration,
        dosing_interval: applicableRegimen.dosing_interval,
        n_doses: nDoses,
        regimen_index: applicableIndex,
        absolute_time_h: absoluteTime,
      };
    }).sort((left: any, right: any) => (left.absolute_time_h ?? 0) - (right.absolute_time_h ?? 0));

    // 验证所有 time_after_dose 有效
    const invalidSample = processedTdmData.find((t: any) => t.time_after_dose < 0);
    if (invalidSample) {
      message.warning('存在采样时间无效的记录，请检查采样时间是否在给药开始之后');
      return;
    }

    setLoading(true);
    setResult(null);
    setSelectedRegimen(null);
    setSubmittedTdmData(processedTdmData);
    setSubmittedRegimens(serializedRegimens);
    setSubmittedModelId(model_id);
    setSubmittedTargetAucMic(selectedMethod === 'AUC/MIC' ? normalizeFiniteNumber(values.target_auc_mic) : null);
    setModifiedRegimenDoses({});
    setModifiedRegimenInfusions({});
    setRegimenRecalcOverrides({});
    setRegimenRecalcStatus({});
    regimenLatestRequestRef.current = {};
    Object.values(regimenDebounceRefs.current).forEach((timer) => clearTimeout(timer));
    regimenDebounceRefs.current = {};
    try {
      const optimizePayload = {
        case_id: caseId,
        model_id,
        administration_mode: tdmAdministrationMode,
        future_infusion_duration_h: !isContinuousMode
          && !hasZeroInputDuration
          && futureInfusionTouchedRef.current
          ? resolvedFutureInfusion ?? undefined
          : undefined,
        tdm_data: processedTdmData,
        dosing_regimens: serializedRegimens.map((regimen: any) => ({
          ...regimen,
          administration_mode: tdmAdministrationMode,
          infusion_duration: hasZeroInputDuration ? 0 : regimen.infusion_duration,
        })),
        ...buildOptimizeTargetFields(selectedMethod, values, futureMicTouchedRef.current),
      };
      const isAdminReplay = replayDraft?.source === 'admin-data-query';
      const startedTask = isAdminReplay
        ? await adminAPI.startReplayOptimize(optimizePayload)
        : await doseAPI.startOptimize(optimizePayload);
      let currentTask = startedTask;
      const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));
      for (let attempt = 0; attempt < 180; attempt += 1) {
        if (currentTask.status === 'completed') break;
        if (currentTask.status === 'failed') {
          throw new Error(currentTask.error || '优化计算失败');
        }
        await sleep(2000);
        currentTask = isAdminReplay
          ? await adminAPI.getReplayOptimizeTask(startedTask.task_id)
          : await doseAPI.getOptimizeTask(startedTask.task_id);
      }
      if (currentTask.status !== 'completed' || !currentTask.result) {
        throw new Error('优化计算仍在执行，请稍后查看结果');
      }
      const res = currentTask.result;
      setResult(res);
      message.success('剂量优化完成');
    } catch (err: any) {
      const isTimeout = err?.code === 'ECONNABORTED' || String(err?.message || '').toLowerCase().includes('timeout');
      const detail = err?.response?.data?.detail || err?.message;
      if (!isTimeout && detail) {
        message.error(detail);
        return;
      }
      message.error(
        isTimeout
          ? '优化计算耗时较长，前端等待超时；后端可能仍在完成计算，请稍后查看历史记录。'
          : (err?.response?.data?.detail || '优化计算失败'),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!displayResult?.calculation_id) return;
    if (!shouldShowTdmDetails) {
      message.warning('请先选择一个候选给药方案');
      return;
    }
    if (selectedRegimen !== null && regimenRecalcStatus[selectedRegimen] === 'loading') {
      message.warning('当前候选方案正在完整重算，请等待完成后再生成报告');
      return;
    }
    if (selectedRegimen !== null && regimenRecalcStatus[selectedRegimen] === 'error') {
      message.error('当前候选方案重算失败，请修正输入并重试后再生成报告');
      return;
    }
    const reportRegimenHistory = submittedRegimens?.length
      ? submittedRegimens
      : displayResult.regimen_history;
    // 构建包含最新解读和曲线的结果
    const finalResult = {
      ...displayResult,
      pkpd_results: normalizePkpdResults(displayResult.pkpd_results, selectedMethod),
      report_has_selected_regimen: hasSelectedRegimen,
      report_selected_regimen: hasSelectedRegimen ? {
        ...displayResult,
        pkpd_results: normalizePkpdResults(displayResult.pkpd_results, selectedMethod),
      } : null,
      ...(canonicalSimulationData ? { simulation_data: canonicalSimulationData } : {}),
      ...(canonicalCombinedCurveData ? { combined_curve: canonicalCombinedCurveData } : {}),
      ...(canonicalTdmPoints.length > 0 ? { tdm_points: canonicalTdmPoints } : {}),
      ...(canonicalSubmittedTdmData?.length ? { tdm_data: canonicalSubmittedTdmData } : {}),
      ...(reportRegimenHistory ? { regimen_history: reportRegimenHistory } : {}),
      ...(editedInterpretation !== undefined
        ? { clinical_interpretation: editedInterpretation }
        : displayInterpretation !== undefined
          ? { clinical_interpretation: displayInterpretation }
          : {}),
    };
    // 如果有回调，走父组件的报告预览流程
    if (onCalculated) {
      onCalculated(finalResult);
      return;
    }
    // 否则直接在新窗口打开报告
    try {
      const regimenOverride = {
        report_has_selected_regimen: hasSelectedRegimen,
        report_selected_regimen: hasSelectedRegimen ? {
          ...displayResult,
          pkpd_results: normalizePkpdResults(displayResult.pkpd_results, selectedMethod),
        } : null,
        administration_mode: tdmAdministrationMode,
        dose: displayResult.recommended_dose_mg,
        dose_kg: displayResult.recommended_dose_mg_kg,
        interval: displayResult.dosing_interval_h,
        infusion_h: displayResult.infusion_duration_h,
        infusion_duration_source: displayResult.infusion_duration_source,
        auc24: displayResult.predicted_auc24,
        maintenance_daily_dose_mg: displayResult.maintenance_daily_dose_mg,
        maintenance_rate_mg_h: displayResult.maintenance_rate_mg_h,
        predicted_css: displayResult.predicted_css,
        predicted_cmin: displayResult.predicted_cmin,
        predicted_cmax: displayResult.predicted_cmax,
        recommended_loading_dose_mg: displayResult.recommended_loading_dose_mg,
        recommended_loading_dose_mg_kg: displayResult.recommended_loading_dose_mg_kg,
        bag_interval_h: displayResult.bag_interval_h,
        evaluation: displayResult.evaluation,
        pkpd_results: normalizePkpdResults(displayResult.pkpd_results, selectedMethod),
        primary_method: selectedMethod,
        safety_check: displayResult.safety_check,
        simulation_data: canonicalSimulationData,
      };
      const generateReport = replayDraft?.source === 'admin-data-query'
        ? adminAPI.replayReportGenerate
        : reportAPI.generate;
      const html = await generateReport(
        displayResult.calculation_id,
        finalResult.clinical_interpretation || undefined,
        regimenOverride,
        canonicalCombinedCurveData || undefined,
        canonicalTdmPoints.length > 0 ? canonicalTdmPoints : undefined,
        reportRegimenHistory || undefined,
        canonicalSubmittedTdmData || undefined,
      );
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(html);
        printWindow.document.close();
      }
    } catch (err: any) {
      message.error('报告生成失败: ' + (err?.response?.data?.detail || err.message));
    }
  };

  const targetAucMicForTable = useMemo(() => {
    if (selectedMethod !== 'AUC/MIC') return null;
    const submitted = normalizeFiniteNumber(submittedTargetAucMic);
    if (submitted != null && submitted > 0) return submitted;
    const ptaContext = (result as any)?.pta_context || {};
    const contextMin = normalizeFiniteNumber(ptaContext.target_min);
    const contextMax = normalizeFiniteNumber(ptaContext.target_max);
    if (ptaContext.target_is_ratio && contextMin != null && contextMin > 0) {
      return contextMax != null && contextMax > 0 ? (contextMin + contextMax) / 2 : contextMin;
    }
    const aucMicResult = normalizePkpdResults(result?.pkpd_results, selectedMethod)?.['AUC/MIC'];
    const resultMin = normalizeFiniteNumber(aucMicResult?.target_min);
    const resultMax = normalizeFiniteNumber(aucMicResult?.target_max);
    if (resultMin != null && resultMin > 0) {
      return resultMax != null && resultMax > 0 ? (resultMin + resultMax) / 2 : resultMin;
    }
    return null;
  }, [result, selectedMethod, submittedTargetAucMic]);
  const targetAucRangeForSummary = useMemo(() => {
    if (selectedMethod !== 'Target AUC') return null;
    const aucResult = normalizePkpdResults(result?.pkpd_results, selectedMethod)?.['Target AUC'];
    const targetMin = normalizeFiniteNumber(aucResult?.target_min);
    const targetMax = normalizeFiniteNumber(aucResult?.target_max);
    if (targetMin == null || targetMin <= 0) return null;
    const displayMin = convertCanonicalValue(targetMin, aucUnit, 'auc');
    const displayMax = convertCanonicalValue(targetMax, aucUnit, 'auc');
    if (targetMax != null && targetMax > 0) {
      return `${Number(displayMin).toFixed(1)} - ${Number(displayMax).toFixed(1)} ${aucUnit}`;
    }
    return `≥${Number(displayMin).toFixed(1)} ${aucUnit}`;
  }, [aucUnit, result?.pkpd_results, selectedMethod]);

  // 按间隔降序排列的候选方案，并合并网格搜索结果
  const baseRegimens = useMemo(() => {
    const normalizeGrid = (gridForMethod: any[]) =>
      gridForMethod.map((g: any) => normalizeCandidateRegimen({
        dose: g.dose_mg,
        dose_kg: g.dose_mg_kg,
        interval: g.interval_h,
        infusion_h: g.infusion_h ?? result?.infusion_duration_h,
        infusion_duration_source: g.infusion_duration_source ?? result?.infusion_duration_source,
        auc24: g.auc24, predicted_auc24: g.auc24, auc24_mic: g.auc24_mic,
        target_auc_mic: targetAucMicForTable,
        predicted_cmin: g.predicted_cmin, predicted_cmax: g.predicted_cmax,
        pta: g.pta, ft_above_mic: g.ft_above_mic,
        target_met: g.target_met,
        target_met_raw: g.target_met_raw,
        target_status: g.target_status,
        pta_met: g.pta_met,
        evaluation: g.evaluation,
        max_dose_exceeded: g.max_dose_exceeded,
        is_optimal: g.is_optimal ?? g.target_met,
        pkpd_results: normalizePkpdResults(g.pkpd_results, selectedMethod),
      }, selectedMethod, targetAucMicForTable));

    const patchFields = (r: any) => {
      return normalizeCandidateRegimen(r, selectedMethod, targetAucMicForTable);
    };

    if (!result?.all_regimens || result.all_regimens.length === 0) {
      const gf = result?.grid_search_results?.[selectedMethod];
      return gf?.length ? normalizeGrid(gf) : [];
    }
    const sorted = [...result.all_regimens]
      .map(patchFields)
      .sort((a: any, b: any) => (Number(b.interval) || 0) - (Number(a.interval) || 0));

    const gf = result.grid_search_results?.[selectedMethod];
    if (!gf?.length) return sorted;

    const existingKeys = new Set(sorted.map((r: any) => `${r.dose}-${r.interval}`));
    const extra = normalizeGrid(gf).filter((g: any) => !existingKeys.has(`${g.dose}-${g.interval}`));
    return [...sorted, ...extra];
  }, [result?.all_regimens, result?.grid_search_results, selectedMethod, targetAucMicForTable]);

  // 当前展示的方案由用户在候选列表中明确选择。
  const recalcCandidateRegimen = React.useCallback(async (
    index: number,
    doseMg: number,
    requestedInfusionH: number,
    infusionSource: 'user_input' | 'drug_config_default' | 'administration_mode' | undefined,
    regimen: any,
    curveOnly = false,
  ) => {
    if (!submittedModelId) return;
    const normalizedRegimen = normalizeCandidateRegimen(regimen, selectedMethod, targetAucMicForTable);
    const intervalH = isContinuousMode ? 24 : normalizeFiniteNumber(normalizedRegimen.interval);
    const infusionH = isContinuousMode
      ? 24
      : (hasZeroInputDuration ? 0 : normalizeFiniteNumber(requestedInfusionH));
    if (
      !Number.isFinite(doseMg)
      || doseMg <= 0
      || intervalH == null
      || intervalH <= 0
      || infusionH == null
      || (!hasZeroInputDuration && infusionH <= 0)
    ) {
      setRegimenRecalcStatus((prev) => ({ ...prev, [index]: 'error' }));
      return;
    }
    const requestKey = `${doseMg}:${infusionH}:${infusionSource ?? 'unspecified'}:${curveOnly ? 'curve' : 'full'}`;
    regimenLatestRequestRef.current[index] = requestKey;
    setRegimenRecalcStatus((prev) => ({ ...prev, [index]: 'loading' }));
    try {
      const recalcPayload = {
        case_id: caseId,
        model_id: submittedModelId,
        dose_mg: doseMg,
        interval_h: intervalH,
        selected_method: selectedMethod,
        administration_mode: tdmAdministrationMode,
        tdm_data: submittedTdmData || undefined,
        dosing_regimens: submittedRegimens || undefined,
        infusion_h: infusionH,
        infusion_duration_source: infusionSource,
        mic: normalizeFiniteNumber(normalizedRegimen.mic ?? result?.mic) ?? undefined,
        mic_source: normalizedRegimen.mic_source ?? result?.mic_source,
        n_sim_doses: isContinuousMode ? 10 : calcSimDoses(intervalH),
        pta_context: result?.pta_context,
        map_parameters: result?.map_parameters,
        curve_only: curveOnly,
      };
      const res = replayDraft?.source === 'admin-data-query'
        ? await adminAPI.replayRecalcRegimen(recalcPayload)
        : await doseAPI.recalcRegimen(recalcPayload);
      if (regimenLatestRequestRef.current[index] !== requestKey) return;
      setRegimenRecalcOverrides((prev) => ({
        ...prev,
        [index]: {
          ...res,
          __sourceDoseMg: doseMg,
          __sourceInfusionH: infusionH,
        },
      }));
      setRegimenRecalcStatus((prev) => ({ ...prev, [index]: 'done' }));
    } catch (error) {
      // Keep the inline status visible while still surfacing diagnostics in development tools.
      console.error('Candidate regimen recalculation failed', error);
      if (regimenLatestRequestRef.current[index] !== requestKey) return;
      const detail = (error as any)?.response?.data?.detail;
      message.error(`候选方案 #${index + 1} 重算失败：${typeof detail === 'string' ? detail : '请检查剂量与输注时间'}`);
      setRegimenRecalcStatus((prev) => ({ ...prev, [index]: 'error' }));
      setRegimenRecalcOverrides((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  }, [caseId, hasZeroInputDuration, isContinuousMode, replayDraft?.source, result?.infusion_duration_h, result?.map_parameters, result?.mic, result?.mic_source, result?.pta_context, selectedMethod, submittedModelId, submittedRegimens, submittedTdmData, targetAucMicForTable, tdmAdministrationMode]);

  const scheduleCandidateRecalc = React.useCallback((
    index: number,
    doseMg: number,
    infusionH: number,
    regimen: any,
  ) => {
    const original = baseRegimens[index] || regimen;
    const originalDose = normalizeFiniteNumber(original?.dose);
    const originalInfusion = hasZeroInputDuration
      ? 0
      : normalizeFiniteNumber(original?.infusion_h ?? result?.infusion_duration_h);
    const doseModified = originalDose == null || Math.abs(doseMg - originalDose) > 0.000001;
    const infusionModified = originalInfusion == null || Math.abs(infusionH - originalInfusion) > 0.000001;
    const infusionSource = isContinuousMode || hasZeroInputDuration
      ? 'administration_mode'
      : infusionModified
        ? 'user_input'
        : original?.infusion_duration_source ?? result?.infusion_duration_source;

    setModifiedRegimenDoses((prev) => {
      const next = { ...prev };
      if (doseModified) next[index] = doseMg;
      else delete next[index];
      return next;
    });
    setModifiedRegimenInfusions((prev) => {
      const next = { ...prev };
      if (infusionModified) next[index] = infusionH;
      else delete next[index];
      return next;
    });
    setRegimenRecalcOverrides((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    if (regimenDebounceRefs.current[index]) {
      clearTimeout(regimenDebounceRefs.current[index]);
    }
    if (!doseModified && !infusionModified) {
      delete regimenDebounceRefs.current[index];
      delete regimenLatestRequestRef.current[index];
      setRegimenRecalcStatus((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      return;
    }
    setRegimenRecalcStatus((prev) => ({ ...prev, [index]: 'loading' }));
    regimenDebounceRefs.current[index] = setTimeout(() => {
      void recalcCandidateRegimen(index, doseMg, infusionH, infusionSource, original);
    }, 300);
  }, [baseRegimens, hasZeroInputDuration, isContinuousMode, recalcCandidateRegimen, result?.infusion_duration_h, result?.infusion_duration_source]);

  const handleCandidateDoseChange = React.useCallback((index: number, value: number | null, regimen: any) => {
    if (value == null) return;
    const infusionH = hasZeroInputDuration
      ? 0
      : normalizeFiniteNumber(modifiedRegimenInfusions[index] ?? regimen.infusion_h ?? result?.infusion_duration_h);
    if (infusionH == null || (!hasZeroInputDuration && infusionH <= 0)) return;
    scheduleCandidateRecalc(index, value, infusionH, regimen);
  }, [hasZeroInputDuration, modifiedRegimenInfusions, result?.infusion_duration_h, scheduleCandidateRecalc]);

  const handleCandidateInfusionChange = React.useCallback((index: number, value: number | null, regimen: any) => {
    if (value == null || value <= 0) return;
    const doseMg = normalizeFiniteNumber(modifiedRegimenDoses[index] ?? regimen.dose);
    if (doseMg == null || doseMg <= 0) return;
    scheduleCandidateRecalc(index, doseMg, value, regimen);
  }, [modifiedRegimenDoses, scheduleCandidateRecalc]);

  const regimens = useMemo(() => baseRegimens.map((regimen: any, index: number) => {
    const override = regimenRecalcOverrides[index];
    const modifiedDose = modifiedRegimenDoses[index];
    const modifiedInfusion = modifiedRegimenInfusions[index];
    const effectiveDose = modifiedDose ?? regimen.dose;
    const effectiveInfusion = hasZeroInputDuration
      ? 0
      : (modifiedInfusion ?? regimen.infusion_h ?? result?.infusion_duration_h);
    const weight = Number(covariates?.weight || 0);
    const effectiveDoseKg = weight > 0 ? +(effectiveDose / weight).toFixed(1) : regimen.dose_kg;
    const normalizedPkpd = normalizePkpdResults(override?.pkpd_results ?? regimen.pkpd_results, selectedMethod);
    const selectedPkpd = normalizedPkpd[selectedMethod];
    const aucMicPkpd = normalizedPkpd['AUC/MIC'];
    const resolvedAuc24Mic = aucMicPkpd?.value
      ?? aucMicPkpd?.details?.auc24_mic
      ?? (selectedMethod === 'AUC/MIC' ? selectedPkpd?.value ?? selectedPkpd?.details?.auc24_mic : undefined)
      ?? regimen.auc24_mic;

    return {
      ...regimen,
      dose: effectiveDose,
      dose_kg: override?.dose_mg_kg ?? effectiveDoseKg,
      interval: override?.interval_h ?? regimen.interval,
      infusion_h: hasZeroInputDuration ? 0 : (override?.infusion_h ?? effectiveInfusion),
      infusion_duration_h: hasZeroInputDuration ? 0 : (override?.infusion_duration_h ?? effectiveInfusion),
      infusion_duration_source: override?.infusion_duration_source
        ?? (modifiedInfusion != null ? 'user_input' : regimen.infusion_duration_source ?? result?.infusion_duration_source),
      mic: override?.mic ?? regimen.mic ?? result?.mic,
      mic_source: override?.mic_source ?? regimen.mic_source ?? result?.mic_source,
      predicted_auc24: override?.predicted_auc24 ?? regimen.predicted_auc24 ?? regimen.auc24,
      maintenance_daily_dose_mg: override?.maintenance_daily_dose_mg ?? regimen.maintenance_daily_dose_mg ?? result?.maintenance_daily_dose_mg,
      maintenance_rate_mg_h: override?.maintenance_rate_mg_h ?? regimen.maintenance_rate_mg_h ?? result?.maintenance_rate_mg_h,
      predicted_css: override?.predicted_css ?? regimen.predicted_css ?? result?.predicted_css,
      predicted_cmin: override?.predicted_cmin ?? regimen.predicted_cmin,
      predicted_cmax: override?.predicted_cmax ?? regimen.predicted_cmax,
      recommended_loading_dose_mg: override?.recommended_loading_dose_mg ?? regimen.recommended_loading_dose_mg ?? result?.recommended_loading_dose_mg,
      recommended_loading_dose_mg_kg: override?.recommended_loading_dose_mg_kg ?? regimen.recommended_loading_dose_mg_kg ?? result?.recommended_loading_dose_mg_kg,
      bag_interval_h: override?.bag_interval_h ?? regimen.bag_interval_h ?? result?.bag_interval_h,
      auc24_mic: resolvedAuc24Mic,
      target_auc_mic: targetAucMicForTable,
      pta: override?.pta ?? regimen.pta,
      evaluation: override?.evaluation ?? regimen.evaluation,
      target_met_raw: override?.target_met_raw
        ?? selectedPkpd?.details?.target_met_raw
        ?? selectedPkpd?.target_met_raw
        ?? regimen.target_met_raw,
      target_status: override?.target_status
        ?? selectedPkpd?.details?.target_status
        ?? selectedPkpd?.target_status
        ?? regimen.target_status,
      max_dose_exceeded: override?.max_dose_exceeded
        ?? selectedPkpd?.details?.max_dose_exceeded
        ?? selectedPkpd?.max_dose_exceeded
        ?? regimen.max_dose_exceeded,
      safety_check: override?.safety_check ?? regimen.safety_check ?? result?.safety_check,
      pkpd_results: normalizedPkpd,
      pk_parameters: override?.pk_parameters ?? regimen.pk_parameters ?? result?.pk_parameters,
      pta_context: override?.pta_context ?? regimen.pta_context ?? result?.pta_context,
      pta_meta: override?.pta_meta ?? regimen.pta_meta ?? result?.pta_meta,
      simulation_data: override?.simulation_data ?? regimen.simulation_data,
      combined_curve: override?.combined_curve ?? regimen.combined_curve,
      tdm_points: override?.tdm_points ?? regimen.tdm_points,
      regimen_history: override?.regimen_history ?? regimen.regimen_history,
      _modified: modifiedDose != null || modifiedInfusion != null,
      _recalc_loading: regimenRecalcStatus[index] === 'loading',
    };
  }), [baseRegimens, covariates?.weight, hasZeroInputDuration, modifiedRegimenDoses, modifiedRegimenInfusions, regimenRecalcOverrides, regimenRecalcStatus, result?.infusion_duration_h, result?.infusion_duration_source, selectedMethod, targetAucMicForTable]);

  const handleCandidateSelect = React.useCallback((index: number) => {
    setSelectedRegimen(index);
    const regimen = regimens[index];
    const existingCurve = regimenRecalcOverrides[index]?.combined_curve ?? regimen?.combined_curve;
    if (
      !regimen
      || isModelBasedCombinedCurve(existingCurve)
      || regimenRecalcStatus[index] === 'loading'
    ) {
      return;
    }
    const doseMg = normalizeFiniteNumber(regimen.dose);
    if (doseMg == null || doseMg <= 0) {
      setRegimenRecalcStatus((previous) => ({ ...previous, [index]: 'error' }));
      return;
    }
    const infusionH = isContinuousMode
      ? 24
      : (hasZeroInputDuration ? 0 : normalizeFiniteNumber(regimen.infusion_h));
    if (infusionH == null || (!hasZeroInputDuration && infusionH <= 0)) {
      setRegimenRecalcStatus((previous) => ({ ...previous, [index]: 'error' }));
      return;
    }
    void recalcCandidateRegimen(
      index,
      doseMg,
      infusionH,
      isContinuousMode || hasZeroInputDuration ? 'administration_mode' : regimen.infusion_duration_source,
      regimen,
      true,
    );
  }, [hasZeroInputDuration, isContinuousMode, recalcCandidateRegimen, regimenRecalcOverrides, regimenRecalcStatus, regimens]);

  const selectedRegimenOverride = useMemo(
    () => (selectedRegimen !== null ? regimenRecalcOverrides[selectedRegimen] ?? null : null),
    [regimenRecalcOverrides, selectedRegimen],
  );

  const originalRegimenResult = useMemo(() => {
    if (!result?.original_regimen) return null;
    const original: any = result.original_regimen;
    const originalPkpd = normalizePkpdResults(
      result.original_regimen_pkpd ?? (original as any).pkpd_results ?? result.pkpd_results,
      selectedMethod,
    );
    const primaryPkpd = originalPkpd?.[selectedMethod];
    return {
      ...result,
      recommended_dose_mg: original.dose_mg ?? original.dose ?? result.recommended_dose_mg,
      recommended_dose_mg_kg: original.dose_mg_kg ?? result.recommended_dose_mg_kg,
      dosing_interval_h: original.interval_h ?? original.interval ?? result.dosing_interval_h,
      infusion_duration_h: hasZeroInputDuration ? 0 : (original.infusion_h ?? original.infusion_duration_h ?? result.infusion_duration_h),
      infusion_duration_source: original.infusion_duration_source ?? result.infusion_duration_source,
      mic: original.mic ?? result.mic,
      mic_source: original.mic_source ?? result.mic_source,
      predicted_auc24: original.predicted_auc24 ?? original.auc24 ?? result.predicted_auc24,
      maintenance_daily_dose_mg: original.maintenance_daily_dose_mg ?? result.maintenance_daily_dose_mg,
      maintenance_rate_mg_h: original.maintenance_rate_mg_h ?? result.maintenance_rate_mg_h,
      predicted_css: original.predicted_css ?? result.predicted_css,
      predicted_cmin: original.predicted_cmin ?? result.predicted_cmin,
      predicted_cmax: original.predicted_cmax ?? result.predicted_cmax,
      auc24_mic: original.auc24_mic ?? primaryPkpd?.value ?? primaryPkpd?.details?.auc24_mic ?? result.auc24_mic,
      target_auc_mic: targetAucMicForTable,
      pta: original.pta ?? primaryPkpd?.pta ?? (result as any).pta,
      evaluation: original.evaluation ?? result.evaluation,
      target_met_raw: original.target_met_raw ?? primaryPkpd?.details?.target_met_raw ?? primaryPkpd?.target_met_raw ?? result.target_met_raw,
      target_status: original.target_status ?? primaryPkpd?.target_status ?? result.target_status,
      max_dose_exceeded: original.max_dose_exceeded ?? primaryPkpd?.details?.max_dose_exceeded ?? result.max_dose_exceeded,
      safety_check: original.safety_check ?? result.safety_check,
      pkpd_results: originalPkpd,
      simulation_data: original.simulation_data ?? result.current_regimen_curve,
      combined_curve: original.combined_curve,
      tdm_points: original.tdm_points ?? result.tdm_points,
      regimen_history: result.regimen_history,
    };
  }, [hasZeroInputDuration, result, selectedMethod, targetAucMicForTable]);

  const originalRegimenStatus = useMemo(
    () => (originalRegimenResult
      ? resolveRegimenStatus({ regimen: originalRegimenResult, selectedMethod, drugConfig })
      : null),
    [drugConfig, originalRegimenResult, selectedMethod],
  );
  const hasSelectedRegimen = selectedRegimen !== null && !!regimens[selectedRegimen];
  const hasOriginalNoAdjustment = !hasSelectedRegimen && !!originalRegimenStatus?.clinicallyMet;
  // 未选择候选方案时，详情始终基于原给药方案展示；是否达标只影响提示文案，
  // 不应导致页面提前使用后台推荐候选方案或组合曲线。
  const shouldShowTdmDetails = !!result;

  const displayResult = useMemo(() => {
    if (!result) return null;
    if (selectedRegimen !== null && regimens[selectedRegimen]) {
      const r = regimens[selectedRegimen];
      const override = selectedRegimenOverride;
      const normalizedPkpdResults = normalizePkpdResults(
        override?.pkpd_results ?? r.pkpd_results ?? result.pkpd_results,
        selectedMethod,
      );
      const useResultCurve = regimenMatchesResultCurve(r, result, hasZeroInputDuration);
      return {
        ...result,
        recommended_dose_mg: r.dose,
        recommended_dose_mg_kg: override?.dose_mg_kg ?? r.dose_kg,
        dosing_interval_h: override?.interval_h ?? r.interval,
        infusion_duration_h: hasZeroInputDuration ? 0 : (override?.infusion_h ?? r.infusion_h ?? result.infusion_duration_h),
        infusion_duration_source: r.infusion_duration_source ?? result.infusion_duration_source,
        mic: override?.mic ?? r.mic ?? result.mic,
        mic_source: override?.mic_source ?? r.mic_source ?? result.mic_source,
        predicted_auc24: override?.predicted_auc24 ?? r.predicted_auc24 ?? r.auc24,
        maintenance_daily_dose_mg: override?.maintenance_daily_dose_mg ?? r.maintenance_daily_dose_mg ?? result.maintenance_daily_dose_mg,
        maintenance_rate_mg_h: override?.maintenance_rate_mg_h ?? r.maintenance_rate_mg_h ?? result.maintenance_rate_mg_h,
        predicted_css: override?.predicted_css ?? r.predicted_css ?? result.predicted_css,
        predicted_cmin: override?.predicted_cmin ?? r.predicted_cmin ?? result.predicted_cmin,
        predicted_cmax: override?.predicted_cmax ?? r.predicted_cmax ?? result.predicted_cmax,
        recommended_loading_dose_mg: override?.recommended_loading_dose_mg ?? r.recommended_loading_dose_mg ?? result.recommended_loading_dose_mg,
        recommended_loading_dose_mg_kg: override?.recommended_loading_dose_mg_kg ?? r.recommended_loading_dose_mg_kg ?? result.recommended_loading_dose_mg_kg,
        bag_interval_h: override?.bag_interval_h ?? r.bag_interval_h ?? result.bag_interval_h,
        auc24_mic: r.auc24_mic ?? result.auc24_mic,
        target_auc_mic: r.target_auc_mic ?? targetAucMicForTable,
        pta: override?.pta ?? r.pta,
        evaluation: override ? override.evaluation : (r.evaluation ?? result.evaluation),
        target_met_raw: override?.target_met_raw ?? r.target_met_raw ?? result.target_met_raw,
        target_status: override?.target_status ?? r.target_status ?? result.target_status,
        max_dose_exceeded: override?.max_dose_exceeded ?? r.max_dose_exceeded ?? result.max_dose_exceeded,
        safety_check: override?.safety_check ?? r.safety_check ?? result.safety_check,
        pkpd_results: normalizedPkpdResults,
        pk_parameters: override?.pk_parameters ?? r.pk_parameters ?? result.pk_parameters,
        simulation_data: override?.simulation_data ?? r.simulation_data ?? (useResultCurve ? result.simulation_data : undefined),
        combined_curve: override?.combined_curve ?? r.combined_curve ?? (useResultCurve ? result.combined_curve : undefined),
        tdm_points: override?.tdm_points ?? r.tdm_points ?? result.tdm_points,
        regimen_history: override?.regimen_history ?? r.regimen_history ?? result.regimen_history,
      };
    }
    if (originalRegimenResult) {
      return originalRegimenResult;
    }
    return {
      ...result,
      target_auc_mic: (result as any).target_auc_mic ?? targetAucMicForTable,
      pkpd_results: normalizePkpdResults(result.pkpd_results, selectedMethod),
    };
  }, [hasOriginalNoAdjustment, hasZeroInputDuration, originalRegimenResult, regimens, result, selectedMethod, selectedRegimen, selectedRegimenOverride, targetAucMicForTable]);

  const nSimDoses = useMemo(() => {
    const interval = normalizeFiniteNumber(displayResult?.dosing_interval_h);
    if (interval == null || interval <= 0) return 0;
    if (isContinuousMode) return 10;
    if (!submittedTdmData || submittedTdmData.length === 0) return calcSimDoses(interval);
    // 计算 TDM 最大绝对时间
    let maxTdmTime = 0;
    submittedTdmData.forEach((t: any) => {
      const absT = normalizeFiniteNumber(t.absolute_time_h);
      if (absT == null) return;
      maxTdmTime = Math.max(maxTdmTime, absT);
    });
    // 谷/峰检测：末次浓度更高时，补偿一个给药间隔
    if (submittedTdmData.length >= 2) {
      const sorted = [...submittedTdmData].sort((a: any, b: any) => {
        const tA = normalizeFiniteNumber(a.absolute_time_h);
        const tB = normalizeFiniteNumber(b.absolute_time_h);
        if (tA == null || tB == null) return 0;
        return tA - tB;
      });
      const last = sorted[sorted.length - 1];
      const prev = sorted[sorted.length - 2];
      if ((last.concentration || 0) > (prev.concentration || 0)) {
        const lastInterval = normalizeFiniteNumber(last.dosing_interval);
        if (lastInterval != null && lastInterval > 0) maxTdmTime += lastInterval;
      }
    }
    return calcSimDoses(interval, maxTdmTime);
  }, [displayResult?.dosing_interval_h, isContinuousMode, submittedTdmData]);

  // 根据选中方案动态生成药时曲线
  const canonicalSimulationData = useMemo(() => {
    if (!result || !displayResult) return null;
    if (selectedRegimen === null) {
      // 原方案是无候选选择状态下唯一允许进入曲线的数据源。
      return resolveCurrentRegimenCurve(result);
    }
    if (submittedRegimens?.length) return null;
    if (selectedRegimenOverride?.simulation_data?.time?.length) return selectedRegimenOverride.simulation_data;
    const selectedCandidate = regimens[selectedRegimen];
    if (selectedCandidate?.simulation_data?.time?.length) return selectedCandidate.simulation_data;
    if (
      isModelBasedCombinedCurve(selectedCandidate?.combined_curve)
      || isModelBasedCombinedCurve(displayResult.combined_curve)
    ) {
      return null;
    }
    if (selectedRegimen !== null) return null;
    if (hasZeroInputDuration) return result.simulation_data;
    const pk = displayResult.pk_parameters || result.pk_parameters;
    const ke = pk?.ke || pk?.ke_individual;
    const vc = pk?.Vc || pk?.Vc_individual;
    if (!ke || !vc || ke <= 0 || vc <= 0) return result.simulation_data;
    return generatePKCurve(
      ke, vc,
      displayResult.recommended_dose_mg,
      displayResult.dosing_interval_h,
      displayResult.infusion_duration_h,
      nSimDoses,
    );
  }, [displayResult, hasZeroInputDuration, nSimDoses, regimens, result, selectedRegimen, selectedRegimenOverride?.simulation_data, submittedRegimens]);

  const displaySimulationData = useMemo(
    () => projectCanonicalConcentrationCurve(canonicalSimulationData, concentrationUnit),
    [canonicalSimulationData, concentrationUnit],
  );
  const currentRegimenPredictionUnavailable = selectedRegimen === null
    && (!canonicalSimulationData || !hasPosteriorPredictionBand(canonicalSimulationData));

  const canonicalSubmittedTdmData = useMemo(
    () => canonicalizeTdmConcentrations(submittedTdmData, concentrationUnit) || [],
    [concentrationUnit, submittedTdmData],
  );

  const canonicalTdmPoints = useMemo(() => {
    if (selectedRegimen !== null && selectedRegimenOverride?.tdm_points?.length) {
      return selectedRegimenOverride.tdm_points;
    }
    if (selectedRegimen === null && displayResult?.tdm_points?.length) {
      return displayResult.tdm_points;
    }
    if (!canonicalSubmittedTdmData.length) return [];
    return buildTdmPoints(
      canonicalSubmittedTdmData,
      selectedRegimen !== null
        ? (selectedRegimenOverride?.regimen_history || displayResult?.regimen_history || submittedRegimens)
        : (displayResult?.regimen_history || submittedRegimens),
    );
  }, [
    displayResult?.regimen_history,
    displayResult?.tdm_points,
    canonicalSubmittedTdmData,
    selectedRegimen,
    selectedRegimenOverride?.regimen_history,
    selectedRegimenOverride?.tdm_points,
    submittedRegimens,
  ]);

  const tdmPoints = useMemo(
    () => projectCanonicalTdmPoints(canonicalTdmPoints, concentrationUnit) || [],
    [canonicalTdmPoints, concentrationUnit],
  );

  const canonicalCombinedCurveData = useMemo(() => {
    if (!result || !displayResult) return null;
    const selectedCandidate = selectedRegimen !== null ? regimens[selectedRegimen] : null;
    // 组合曲线只属于“已明确选中候选方案”的比较状态。
    // 未选择时即使后端返回 combined_curve，也不能把它当成当前方案曲线。
    if (selectedRegimen === null) return null;
    // 用户选择的候选方案如果与原方案完全一致，不应伪造“更换前/更换后”两段曲线。
    if (selectedCandidate && regimenMatchesResultCurve(
      selectedCandidate,
      originalRegimenResult || result,
      hasZeroInputDuration,
    )) return null;
    const fallbackCombinedCurve = (
      selectedRegimenOverride?.combined_curve
      ?? selectedCandidate?.combined_curve
    );
    const fallbackIsModelBased = isModelBasedCombinedCurve(fallbackCombinedCurve);
    if (fallbackCombinedCurve && fallbackIsModelBased) return fallbackCombinedCurve;
    if (hasZeroInputDuration) return null;
    const pk = displayResult.pk_parameters || result.pk_parameters;
    const ke = pk?.ke || pk?.ke_individual;
    const vc = pk?.Vc || pk?.Vc_individual;
    if (!ke || !vc || ke <= 0 || vc <= 0) return fallbackCombinedCurve || null;

    const segments = normalizeDosingSegments(
      selectedRegimen !== null
        ? (
          selectedRegimenOverride?.regimen_history?.length
            ? selectedRegimenOverride.regimen_history
            : (displayResult.regimen_history?.length ? displayResult.regimen_history : submittedRegimens)
        )
        : (displayResult.regimen_history?.length ? displayResult.regimen_history : submittedRegimens),
      submittedTdmData,
    );
    if (segments.length === 0) return fallbackCombinedCurve || null;

    const optInterval = normalizeFiniteNumber(displayResult.dosing_interval_h);
    if (optInterval == null || optInterval <= 0) return null;
    const optNDoses = isContinuousMode ? 10 : Math.ceil(24 / optInterval);
    const observedTdmTimes = canonicalTdmPoints
      .map((point: { time: number }) => point.time)
      .filter((time: number) => Number.isFinite(time));
    const observedThroughH = Math.max(
      0,
      ...observedTdmTimes,
    );

    const optInfusion = normalizeFiniteNumber(displayResult.infusion_duration_h);
    if (optInfusion == null || (!hasZeroInputDuration && optInfusion <= 0)) return null;
    return generateCombinedPKCurveMulti(
      ke, vc,
      segments,
      displayResult.recommended_dose_mg, optInterval, optInfusion, optNDoses,
      0.1,
      { observedThroughH, concentrationScale: 1 },
    );
  }, [
    displayResult,
    isContinuousMode,
    hasZeroInputDuration,
    result,
    selectedRegimen,
    regimens,
    selectedRegimenOverride?.combined_curve,
    selectedRegimenOverride?.regimen_history,
    submittedRegimens,
    submittedTdmData,
    canonicalTdmPoints,
  ]);

  const combinedCurveData = useMemo(
    () => projectCanonicalConcentrationCurve(canonicalCombinedCurveData, concentrationUnit),
    [canonicalCombinedCurveData, concentrationUnit],
  );

  const [regenInterpretation, setRegenInterpretation] = useState<string | undefined>(undefined);
  const [interpretLoading, setInterpretLoading] = useState(false);
  const [editedInterpretation, setEditedInterpretation] = useState<string | undefined>(undefined);
  const previousAdministrationModeRef = useRef<AdministrationMode>(tdmAdministrationMode);

  useEffect(() => {
    const modeChanged = previousAdministrationModeRef.current !== tdmAdministrationMode;
    previousAdministrationModeRef.current = tdmAdministrationMode;

    if (modeChanged) {
      setResult(null);
      setSelectedRegimen(null);
      setSubmittedTdmData(null);
      setSubmittedRegimens(null);
      setSubmittedModelId(null);
      setSubmittedTargetAucMic(null);
      setModifiedRegimenDoses({});
      setModifiedRegimenInfusions({});
      setRegimenRecalcOverrides({});
      setRegimenRecalcStatus({});
      setRegenInterpretation(undefined);
      setInterpretLoading(false);
      setEditedInterpretation(undefined);
      regimenLatestRequestRef.current = {};
      Object.values(regimenDebounceRefs.current).forEach((timer) => clearTimeout(timer));
      regimenDebounceRefs.current = {};
      form.setFieldsValue({
        model_id: undefined,
        dosing_regimens: undefined,
        tdm_samples: undefined,
      });
    }

    const defaultInterval = isContinuousMode
      ? drugConfig?.continuous_infusion?.default_bag_interval_h
      : undefined;
    const defaultInfusionDuration = isContinuousMode
      ? 24
      : hasZeroInputDuration
        ? 0
        : (replayDraft?.futureInfusionDurationH ?? drugConfig?.default_infusion_duration_h);
    const currentFutureInfusionDuration = form.getFieldValue('future_infusion_duration_h');
    if (modeChanged || currentFutureInfusionDuration == null) {
      form.setFieldValue('future_infusion_duration_h', defaultInfusionDuration);
    }
    const currentRegimens = form.getFieldValue('dosing_regimens');

    if (!Array.isArray(currentRegimens) || currentRegimens.length === 0) {
      form.setFieldsValue({
        dosing_regimens: [
          {
            dose: undefined,
            infusion_duration: defaultInfusionDuration,
            dosing_interval: defaultInterval,
            n_doses: undefined,
            n_doses_source: 'auto',
          },
        ],
      });
      return;
    }

    const normalizedRegimens = currentRegimens.map((regimen: any) => ({
      ...regimen,
      infusion_duration: isContinuousMode
        ? 24
        : hasZeroInputDuration
          ? 0
        : (regimen?.infusion_duration == null || regimen.infusion_duration === 24
          ? defaultInfusionDuration
          : regimen.infusion_duration),
      dosing_interval: regimen?.dosing_interval ?? defaultInterval,
    }));

    if (JSON.stringify(normalizedRegimens) !== JSON.stringify(currentRegimens)) {
      form.setFieldsValue({ dosing_regimens: normalizedRegimens });
    }
  }, [
    drugConfig?.continuous_infusion?.default_bag_interval_h,
    drugConfig?.default_infusion_duration_h,
    form,
    isContinuousMode,
    hasZeroInputDuration,
    replayDraft?.futureInfusionDurationH,
    tdmAdministrationMode,
  ]);

  useEffect(() => {
    if ((!hasSelectedRegimen && !hasOriginalNoAdjustment) || !result?.calculation_id) {
      setRegenInterpretation(undefined);
      return;
    }
    // 切换方案时重置用户编辑
    setEditedInterpretation(undefined);
    if (!displayResult) return;
    const selectedRegimenForInterpretation = {
      // Keep the selected-state explicit.  Without this flag the API treats
      // the request as a baseline interpretation and may retain the original
      // recommendation (for example, q12h) in the generated text even after
      // the user selects a different candidate (for example, q24h).
      selected_regimen_active: hasSelectedRegimen,
      dose: displayResult.recommended_dose_mg,
      dose_kg: displayResult.recommended_dose_mg_kg,
      interval: displayResult.dosing_interval_h,
      infusion_h: displayResult.infusion_duration_h,
      auc24: displayResult.predicted_auc24,
      strategy_type: displayResult.strategy_type,
      transition_time_h: displayResult.transition_time_h ?? undefined,
      recommended_loading_dose_mg: displayResult.recommended_loading_dose_mg,
      recommended_loading_dose_mg_kg: displayResult.recommended_loading_dose_mg_kg,
      loading_regimen: displayResult.loading_regimen ? {
        enabled: displayResult.loading_regimen.enabled,
        dose_mg: displayResult.loading_regimen.dose_mg,
        dose_mg_kg: displayResult.loading_regimen.dose_mg_kg ?? undefined,
        count: displayResult.loading_regimen.count,
      } : undefined,
      maintenance_regimen: displayResult.maintenance_regimen ? {
        dose_mg: displayResult.maintenance_regimen.dose_mg,
        dose_mg_kg: displayResult.maintenance_regimen.dose_mg_kg ?? undefined,
        interval_h: displayResult.maintenance_regimen.interval_h,
      } : undefined,
    };
    const interpretRequest = replayDraft?.source === 'admin-data-query'
      ? adminAPI.replayInterpret(result.calculation_id, selectedRegimenForInterpretation, selectedMethod)
      : doseAPI.interpret(result.calculation_id, selectedRegimenForInterpretation, selectedMethod);
    let cancelled = false;
    setInterpretLoading(true);
    interpretRequest.then(text => {
      if (cancelled) return;
      setRegenInterpretation(text);
    }).catch(() => {
      if (cancelled) return;
      setRegenInterpretation(result.clinical_interpretation);
    }).finally(() => {
      if (!cancelled) setInterpretLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [displayResult, hasOriginalNoAdjustment, hasSelectedRegimen, replayDraft?.source, result?.calculation_id, result?.clinical_interpretation, selectedMethod]);

  const displayInterpretation = shouldShowTdmDetails
    ? (regenInterpretation ?? result?.clinical_interpretation)
    : undefined;

  const safety = displayResult?.safety_check || { safe: true, warnings: [], errors: [] };
  const continuousPrimaryMetric = useMemo(() => {
    switch (selectedMethod) {
      case 'Target Concentration':
        return {
          label: '预计 Css',
          dataKey: 'predicted_css',
          unit: concentrationUnit,
          decimals: 2,
        };
      case 'Target AUC':
        return {
          label: '预测AUC24',
          dataKey: 'predicted_auc24',
          unit: aucUnit,
          decimals: 1,
        };
      case 'AUC/MIC':
        return {
          label: '预测AUC24/MIC',
          dataKey: 'auc24_mic',
          unit: '',
          decimals: 1,
        };
      case '%fT>MIC':
        return {
          label: '%fT>MIC',
          dataKey: '%fT>MIC',
          unit: '%',
          decimals: 1,
        };
      default:
        return null;
    }
  }, [concentrationUnit, selectedMethod]);
  const methodColumns = useMemo(() => {
    if (isContinuousMode && selectedMethod === 'Target Concentration') {
      return [
        { title: `预计 Css (${concentrationUnit})`, source: 'field', dataKey: 'predicted_css', format: 'number', decimals: 2, width: 70 } as RegimenColumn,
        { title: `泵速 (${doseMassUnit}/h)`, source: 'field', dataKey: 'maintenance_rate_mg_h', format: 'number', decimals: 2, width: 72 } as RegimenColumn,
      ];
    }
    if (isContinuousMode && selectedMethod === 'Target AUC') {
      return [
        { title: `预测AUC24 (${aucUnit})`, source: 'field', dataKey: 'predicted_auc24', format: 'number', decimals: 1, width: 72 } as RegimenColumn,
        { title: `泵速 (${doseMassUnit}/h)`, source: 'field', dataKey: 'maintenance_rate_mg_h', format: 'number', decimals: 2, width: 72 } as RegimenColumn,
      ];
    }
    if (isContinuousMode && selectedMethod === 'AUC/MIC') {
      return [
        { title: '目标AUC₂₄/MIC', source: 'field', dataKey: 'target_auc_mic', format: 'number', decimals: 1, width: 72 } as RegimenColumn,
        { title: `预测AUC24 (${aucUnit})`, source: 'field', dataKey: 'predicted_auc24', format: 'number', decimals: 1, width: 72 } as RegimenColumn,
      ];
    }
    if (isContinuousMode && selectedMethod === '%fT>MIC') {
      return [
        { title: '%fT>MIC (%)', source: 'pkpd', dataKey: 'value', format: 'percent', decimals: 1, width: 68 } as RegimenColumn,
        { title: 'PTA (%)', source: 'pkpd', dataKey: 'pta', format: 'percent', decimals: 1, width: 54 } as RegimenColumn,
        { title: `泵速 (${doseMassUnit}/h)`, source: 'field', dataKey: 'maintenance_rate_mg_h', format: 'number', decimals: 2, width: 72 } as RegimenColumn,
      ];
    }
    return applyConfiguredConcentrationUnits(
      METHOD_CONFIG[selectedMethod]?.regimenColumns || [],
      drugConfig,
      selectedMethod,
      tdmAdministrationMode,
      continuousTargetParameter,
    );
  }, [
    aucUnit,
    concentrationUnit,
    continuousTargetParameter,
    drugConfig,
    doseMassUnit,
    isContinuousMode,
    selectedMethod,
    tdmAdministrationMode,
  ]);
  const hasMethodField = (key: string) => methodColumns.some(
    (column: RegimenColumn) => column.dataKey === key || column.dataKey.includes(key),
  );
  const regimenStatuses = useMemo(
    () => regimens.map((regimen: any) => resolveRegimenStatus({ regimen, selectedMethod, drugConfig })),
    [drugConfig, regimens, selectedMethod],
  );
  const hasClinicallyMetRegimen = regimenStatuses.some((status) => status.clinicallyMet);
  const activeRegimenStatus = useMemo(() => {
    if (selectedRegimen !== null && regimenStatuses[selectedRegimen]) {
      return regimenStatuses[selectedRegimen];
    }
    if (hasOriginalNoAdjustment && originalRegimenStatus) {
      return originalRegimenStatus;
    }
    if (displayResult) {
      return resolveRegimenStatus({ regimen: displayResult, selectedMethod, drugConfig });
    }
    return null;
  }, [displayResult, drugConfig, hasOriginalNoAdjustment, originalRegimenStatus, regimenStatuses, selectedMethod, selectedRegimen]);
  const resolveMethodColumnValue = (regimen: any, col: RegimenColumn, pkpdResults?: Record<string, any>) => {
    let value: any;
    if (col.source === 'field') {
      value = regimen[col.dataKey];
      if (value == null && col.dataKey === 'target_auc_mic') {
        value = targetAucMicForTable;
      }
      if (value == null && col.dataKey === 'predicted_auc24') {
        const aucMicDetails = pkpdResults?.['AUC/MIC']?.details;
        value = regimen.predicted_auc24 ?? regimen.auc24 ?? aucMicDetails?.auc24;
      }
    } else {
      const pkpd = pkpdResults?.[selectedMethod] ?? regimen.pkpd_results?.[selectedMethod];
      if (pkpd) {
        const parts = col.dataKey.split('.');
        value = pkpd as any;
        for (const part of parts) value = value?.[part];
      }
    }
    return value;
  };

  const formatMethodMetric = (regimen: any, col: RegimenColumn) => {
    const value = resolveMethodColumnValue(regimen, col, regimen.pkpd_results);
    if (value == null) return '-';
    const key = col.dataKey.toLowerCase();
    let projected = Number(value);
    if (key.includes('auc') && !key.includes('mic')) {
      projected = convertCanonicalValue(value, aucUnit, 'auc') ?? projected;
    } else if (/(cmin|cmax|css|concentration|trough|peak)/.test(key) && !key.includes('mic')) {
      projected = convertCanonicalValue(value, concentrationUnit, 'concentration') ?? projected;
    } else if (key.includes('rate_mg_h')) {
      projected = convertCanonicalValue(value, `${doseMassUnit}/h`, 'dose_rate') ?? projected;
    }
    const display = formatDisplayNumber(projected, col.decimals ?? 2);
    return col.format === 'percent' ? `${display}%` : display;
  };

  const renderRecalcFeedback = (index: number) => {
    if (regimenRecalcStatus[index] === 'error') {
      return <Text type="danger">{'\u91cd\u7b97\u5931\u8d25'}</Text>;
    }
    if (regimenRecalcStatus[index] === 'loading') {
      return <Text type="secondary">{'\u91cd\u7b97\u4e2d...'}</Text>;
    }
    return null;
  };

  const renderCandidateDoseInput = (
    regimen: any,
    index: number,
    width: React.CSSProperties['width'] = 88,
  ) => (
    <InputNumber
      size="small"
      min={1}
      step={doseMassUnit === 'mg' ? 1 : 0.001}
      precision={doseMassUnit === 'mg' ? 0 : 3}
      value={convertCanonicalValue(modifiedRegimenDoses[index] ?? regimen.dose, doseMassUnit, 'mass') ?? undefined}
      style={{ width }}
      onClick={(event) => event.stopPropagation()}
      onChange={(value) => handleCandidateDoseChange(
        index,
        value == null ? null : convertUnitValue(value, doseMassUnit, 'mg', 'mass'),
        baseRegimens[index] || regimen,
      )}
    />
  );

  const renderCandidateInfusionInput = (
    regimen: any,
    index: number,
    width: React.CSSProperties['width'] = 76,
  ) => (
    <InputNumber
      size="small"
      min={0.1}
      max={normalizeFiniteNumber(regimen.interval) ?? undefined}
      step={0.1}
      precision={2}
      value={modifiedRegimenInfusions[index] ?? normalizeFiniteNumber(regimen.infusion_h) ?? undefined}
      style={{ width }}
      onClick={(event) => event.stopPropagation()}
      onChange={(value) => handleCandidateInfusionChange(index, value, baseRegimens[index] || regimen)}
    />
  );

  const regimenSelectCell = (index: number) => (
    <Radio
      checked={selectedRegimen === index}
      onChange={() => handleCandidateSelect(index)}
      onClick={(event) => event.stopPropagation()}
    />
  );

  const regimenEvaluationCell = (regimen: any, index: number) => {
    const evaluationStatus = resolveEvaluationStatus(regimenStatuses[index], regimen?.pta);
    return regimen._recalc_loading
      ? <Spin size="small" />
      : (
        <Tag
          color={evaluationStatus.color}
          style={{ margin: 0, paddingInline: 5, fontSize: 11, lineHeight: '18px', whiteSpace: 'nowrap', display: 'inline-flex' }}
        >
          {evaluationStatus.label}
        </Tag>
      );
  };

  const formatInfusionSourceLabel = (source: unknown) => {
    if (source === 'user_input') return '用户设置';
    if (source === 'drug_config_default') return '默认';
    if (source === 'administration_mode') return '给药模式';
    return '来源未标记';
  };

  const desktopCandidateColumns = [
    {
      title: '选',
      key: 'select',
      width: 44,
      align: 'center' as const,
      render: (_: any, regimen: any, index: number) => regimenSelectCell(
        resolveCandidateSourceIndex(regimen, index),
      ),
    },
    {
      title: `剂量 (${doseMassUnit})`,
      key: 'dose',
      width: 116,
      render: (_: any, regimen: any, index: number) => (
        <Space direction="vertical" size={2}>
          {renderCandidateDoseInput(regimen, resolveCandidateSourceIndex(regimen, index))}
          <Text type="secondary" style={{ fontSize: 11 }}>{formatResultDosePerKg(regimen.dose_kg)}</Text>
          {renderRecalcFeedback(resolveCandidateSourceIndex(regimen, index))}
        </Space>
      ),
    },
    ...(!isContinuousMode && !hasZeroInputDuration ? [{
      title: '输注 (h)',
      key: 'infusion_h',
      width: 90,
      render: (_: any, regimen: any, index: number) => (
        <Space direction="vertical" size={2}>
          {renderCandidateInfusionInput(regimen, resolveCandidateSourceIndex(regimen, index))}
          <Text type="secondary" style={{ fontSize: 11 }}>
            {formatInfusionSourceLabel(regimen.infusion_duration_source)}
          </Text>
        </Space>
      ),
    }] : []),
    ...methodColumns.map((col: RegimenColumn) => ({
      title: <Tooltip title={col.title}><span>{col.title.replace(/\s*\(.*\)$/, '')}</span></Tooltip>,
      key: col.dataKey,
      width: col.width ?? 76,
      ellipsis: true,
      render: (_: any, regimen: any) => regimen._recalc_loading
        ? <Spin size="small" />
        : <span style={regimen._modified ? { color: '#0B2545', fontWeight: 500 } : undefined}>{formatMethodMetric(regimen, col)}</span>,
    })),
    {
      title: '评价',
      key: 'evaluation',
      width: 100,
      align: 'center' as const,
      className: 'candidate-evaluation-column',
      render: (_: any, regimen: any, index: number) => regimenEvaluationCell(
        regimen,
        resolveCandidateSourceIndex(regimen, index),
      ),
    },
  ];

  const renderMobileCandidateCard = (regimen: any, sourceIndex: number) => {
    const evaluationStatus = resolveEvaluationStatus(regimenStatuses[sourceIndex], regimen?.pta);
    const recalcState: CandidateRegimenRecalcState = (
      regimenRecalcStatus[sourceIndex] === 'loading' || regimen?._recalc_loading
        ? 'loading'
        : regimenRecalcStatus[sourceIndex] === 'error' || regimen?._recalc_error
          ? 'error'
          : 'idle'
    );
    const intervalLabel = isContinuousMode ? '连续输注' : `Q${regimen.interval}h`;

    return (
      <EditableCandidateRegimenCard
        title={formatResultDose(regimen.dose)}
        subtitle={`${intervalLabel} · ${formatResultDosePerKg(regimen.dose_kg)}`}
        metrics={methodColumns.map((col: RegimenColumn) => ({
          key: col.dataKey,
          label: col.title,
          value: formatMethodMetric(regimen, col),
        }))}
        editors={[
          {
            key: 'dose',
            label: `剂量 (${doseMassUnit})`,
            control: renderCandidateDoseInput(regimen, sourceIndex, '100%'),
          },
          ...(!isContinuousMode && !hasZeroInputDuration ? [{
            key: 'infusion',
            label: '输注时间 (h)',
            control: renderCandidateInfusionInput(regimen, sourceIndex, '100%'),
          }] : []),
        ]}
        evaluationLabel={evaluationStatus.label}
        evaluationColor={evaluationStatus.color}
        selected={selectedRegimen === sourceIndex}
        modified={Boolean(regimen?._modified)}
        recalcState={recalcState}
        selectionLabel={`选择 ${intervalLabel}、${formatResultDose(regimen.dose)} 的候选方案`}
        onSelect={() => handleCandidateSelect(sourceIndex)}
      />
    );
  };

  const selectedPlanDailyDose = displayResult?.recommended_dose_mg != null && displayResult?.dosing_interval_h
    ? ((24 / displayResult.dosing_interval_h) * displayResult.recommended_dose_mg).toFixed(1)
    : '-';
  const displayResultPta = (displayResult as any)?.pta;

  const selectedPlanSummary = isContinuousMode
    ? [
      { label: '维持总日剂量', value: displayResult?.maintenance_daily_dose_mg != null ? `${formatResultDose(displayResult.maintenance_daily_dose_mg)}/24h` : 'N/A' },
      { label: '泵速', value: displayResult?.maintenance_rate_mg_h != null ? `${formatResultDose(displayResult.maintenance_rate_mg_h)}/h` : 'N/A' },
      ...(selectedMethod === 'Target Concentration'
        ? [{ label: '预计 Css', value: formatConcentrationValue(displayResult?.predicted_css, 2) }]
        : []),
      ...(selectedMethod === 'Target AUC'
        ? [
          { label: '目标AUC₂₄范围', value: targetAucRangeForSummary || 'N/A' },
          { label: '预测AUC24', value: formatAucValue(displayResult?.predicted_auc24) },
        ]
        : []),
      ...(selectedMethod === 'AUC/MIC'
        ? [
          { label: '目标AUC₂₄/MIC', value: targetAucMicForTable != null ? `${Number(targetAucMicForTable).toFixed(1)}` : 'N/A' },
          { label: '预测AUC24/MIC', value: displayResult?.auc24_mic != null ? `${Number(displayResult.auc24_mic).toFixed(1)}` : 'N/A' },
          { label: '预测AUC24', value: formatAucValue(displayResult?.predicted_auc24) },
        ]
        : []),
      ...(selectedMethod === '%fT>MIC'
        ? [{
          label: '%fT>MIC',
          value: (() => {
            const value = displayResult?.pkpd_results?.['%fT>MIC']?.value;
            return value != null ? `${Number(value).toFixed(1)}%` : 'N/A';
          })(),
        }]
        : []),
      ...(displayResult?.recommended_loading_dose_mg != null
        ? [{ label: '负荷剂量', value: formatResultDose(displayResult.recommended_loading_dose_mg) }]
        : []),
      ...(displayResult?.bag_interval_h != null
        ? [{ label: '连续输注间隔', value: `每 ${displayResult.bag_interval_h} 小时` }]
        : []),
    ]
    : [
      ...(selectedMethod === 'AUC/MIC'
        ? [
          { label: '目标AUC₂₄/MIC', value: targetAucMicForTable != null ? Number(targetAucMicForTable).toFixed(1) : 'N/A' },
          { label: '预测AUC₂₄/MIC', value: displayResult?.auc24_mic != null ? Number(displayResult.auc24_mic).toFixed(1) : 'N/A' },
          { label: '预测AUC₂₄', value: formatAucValue(displayResult?.predicted_auc24) },
        ]
        : []),
      ...(selectedMethod === 'Target AUC'
        ? [
          { label: '目标AUC₂₄范围', value: targetAucRangeForSummary || 'N/A' },
          { label: '预测AUC₂₄', value: formatAucValue(displayResult?.predicted_auc24) },
        ]
        : []),
      { label: 'PTA', value: displayResultPta != null ? `${Number(displayResultPta).toFixed(1)}%` : 'N/A' },
      { label: '预测峰浓度 (Cmax)', value: formatConcentrationValue(displayResult?.predicted_cmax, 1) },
      { label: '预测谷浓度 (Cmin)', value: formatConcentrationValue(displayResult?.predicted_cmin, 1) },
      { label: '给药间隔', value: displayResult?.dosing_interval_h != null ? `每 ${displayResult.dosing_interval_h} 小时` : 'N/A' },
      { label: '24h 总剂量', value: selectedPlanDailyDose !== '-' ? formatResultDose(Number(selectedPlanDailyDose)) : 'N/A' },
    ];

  const handleAdministrationModeChange = (nextMode: AdministrationMode) => {
    if (nextMode === tdmAdministrationMode) return;
    const applyModeChange = () => {
      futureInfusionTouchedRef.current = false;
      futureMicTouchedRef.current = false;
      setAdministrationModeDirty(true);
      setTdmAdministrationMode(nextMode);
    };
    const hasHistory = Boolean(
      result
      || form.getFieldValue('model_id')
      || form.getFieldValue('dosing_regimens')?.length
      || form.getFieldValue('tdm_samples')?.length
    );
    if (!hasHistory) {
      applyModeChange();
      return;
    }
    Modal.confirm({
      title: '切换 TDM 给药方式？',
      content: '为避免混用静脉和口服历史，切换后将清空当前模型、给药方案、TDM 样本和优化结果。',
      okText: '确认切换',
      cancelText: '取消',
      onOk: applyModeChange,
    });
  };

  const handleTargetMethodChange = (nextMethod: string) => {
    if (nextMethod === selectedMethod) return;
    futureMicTouchedRef.current = false;
    setSelectedMethod(nextMethod);
    const configuredMic = normalizeFiniteNumber(drugConfig?.default_mic);
    form.setFieldValue('mic', configuredMic != null && configuredMic > 0 ? configuredMic : undefined);
  };

  return (
    <>
      <style>{TDM_OPTIMIZE_STYLES}</style>
      <Card
	      title={
	        <Space>
	          <ThunderboltOutlined />
	          <span>TDM剂量优化</span>
	          <Tag color={isContinuousMode ? 'blue' : (tdmAdministrationMode === 'oral' ? 'green' : 'default')} style={{ marginInlineStart: 4 }}>
	            {getAdministrationModeLabel(tdmAdministrationMode)}
	          </Tag>
	        </Space>
	      }
      extra={
        <Button type="link" onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? '展开' : '收起'}
        </Button>
      }
      >
      {!collapsed && (
        <>
          {replayDraft ? (
            <Alert
              message="管理员复现模式"
              description="已从结构化详情加载患者协变量、当前给药方案、模型和 TDM 采样记录。请核对后点击“执行优化”进行回测。"
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />
          ) : null}
	          <Alert
	            message="说明"
	            description={isContinuousMode
                ? '输入当前连续输注方案和采样数据，系统将按连续零级输入语义进行优化；维持总日剂量与泵速按连续输注展示，但主靶值仍以当前所选计算方法为准。'
                : isOralMode
                  ? '输入患者当前口服方案和 TDM 监测数据，系统将按口服吸收模型进行个体化优化。'
                  : isBolusMode
                    ? '输入患者当前静脉注射方案和 TDM 监测数据，系统将按静脉快速输入事件进行个体化优化。'
                  : '输入患者当前给药方案和 TDM 监测数据，结合 PopPK 模型获取个体化 PK 参数并优化下一次给药方案。'}
	            type="info"
	            showIcon
	            style={{ marginBottom: 24 }}
	          />
          <Form
            form={form}
            layout="vertical"
            onFinish={handleOptimize}
            onValuesChange={handleFormValuesChange}
            initialValues={{
              dosing_regimens: [
                {
                  dose: undefined,
                  infusion_duration: isContinuousMode ? 24 : (hasZeroInputDuration ? 0 : undefined),
                  dosing_interval: undefined,
                  n_doses: undefined,
                  n_doses_source: 'auto',
                },
              ],
            }}
          >
            {/* Model selection and target parameters - 同一行 */}
            <Row gutter={16} align="bottom">
              {supportedAdministrationModes.length > 1 && (
                <Col xs={24} sm={12} md={6}>
                  <Form.Item label="给药模式">
                    <Radio.Group
                      value={tdmAdministrationMode}
                      onChange={(event) => handleAdministrationModeChange(event.target.value)}
                    >
                      {supportedAdministrationModes.includes('intermittent') && (
                        <Radio.Button value="intermittent">静脉滴注</Radio.Button>
                      )}
                      {supportedAdministrationModes.includes('continuous') && (
                        <Radio.Button value="continuous">连续输注</Radio.Button>
                      )}
                      {supportedAdministrationModes.includes('oral') && (
                        <Radio.Button value="oral">口服</Radio.Button>
                      )}
                      {supportedAdministrationModes.includes('bolus') && (
                        <Radio.Button value="bolus">静脉注射</Radio.Button>
                      )}
                    </Radio.Group>
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} sm={12} md={7}>
                <Form.Item
                  name="model_id"
                  label="选择 PopPK 模型"
                  rules={[{ required: true, message: '请选择模型' }]}
                >
                  <Select
                    placeholder="选择用于优化的 PopPK 模型"
                    loading={modelsLoading}
                    showSearch
                    optionFilterProp="label"
                    options={activeModels.map((m) => ({
                      value: m.model_id,
                      label: shortModelName(m),
                    }))}
                    notFoundContent={modelsLoading ? '加载中...' : '暂无激活的模型'}
                  />
                </Form.Item>
              </Col>
              {availableMethods.length > 1 && (
                <Col xs={24} sm={12} md={7}>
                  <Form.Item label="计算方法">
                    <Radio.Group value={selectedMethod} onChange={e => handleTargetMethodChange(e.target.value)}>
                      {availableMethods.map((m: string) => (
                        <Radio.Button key={m} value={m}>{METHOD_CONFIG[m]?.label || m}</Radio.Button>
                      ))}
                    </Radio.Group>
                  </Form.Item>
                </Col>
              )}
              {(METHOD_CONFIG[selectedMethod]?.inputs || []).map(input => (
                <Col key={input.name} xs={24} sm={12} md={5}>
                  {(() => {
                    const inputLabel = input.name === 'target_cmin' && isContinuousMode ? '目标Css' : input.label;
                    const inputUnit = getConfiguredInputUnit(
                      drugConfig,
                      input,
                      selectedMethod,
                      tdmAdministrationMode,
                      continuousTargetParameter,
                    );
                    const displayLabel = inputUnit ? `${inputLabel} (${inputUnit})` : inputLabel;
                    return (
                  <Form.Item
                    name={input.name}
                    label={displayLabel}
                  >
                    <InputNumber
                      min={0}
                      step={input.name === 'mic' ? 0.125 : (input.step || 1)}
                      style={{ width: '100%' }}
                      placeholder={input.name === 'mic' ? '请输入 MIC' : undefined}
                    />
                  </Form.Item>
                    );
                  })()}
                </Col>
              ))}
            </Row>

            {/* ======== Dosing Regimen Section ======== */}
            <Divider orientation="left" plain>
              <Space><MedicineBoxOutlined /> 当前给药方案</Space>
            </Divider>
            <Alert
              message={isContinuousMode
                ? '请填写当前连续输注方案，如有剂量或泵速调整可添加多个时段。'
                : isOralMode
                  ? '请填写患者当前口服给药方案，如有剂量调整，可添加多个方案。'
                  : isBolusMode
                    ? '请填写患者当前静脉注射方案，如有剂量调整，可添加多个方案。'
                  : '请填写患者当前正在使用的给药方案信息，如有剂量调整，可添加多个方案。'}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              banner
            />

            <Form.List name="dosing_regimens">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Card
                      key={key}
                      size="small"
                      style={{ marginBottom: 12, background: '#f6ffed', border: '1px solid #b7eb8f' }}
                      title={<Text type="secondary">{`给药方案 #${name + 1}`}</Text>}
                      extra={
                        fields.length > 1 ? (
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => remove(name)}
                            size="small"
                          >
                            删除
                          </Button>
                        ) : null
                      }
                    >
                      <Row gutter={16}>
                        <Col xs={24} sm={12} md={5}>
                          <Form.Item
                            {...restField}
                            name={[name, 'dosing_start_time']}
                            label="开始给药时间"
                            rules={[{ required: true, message: '请选择开始给药时间' }]}
                            tooltip="该方案首次给药的日期和时间"
                          >
                            <DatePicker
                              showTime={{ format: 'HH:mm' }}
                              format="YYYY-MM-DD HH:mm"
                              style={{ width: '100%' }}
                              placeholder="选择日期时间"
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={5}>
                          <Form.Item
                            {...restField}
                            name={[name, 'dose']}
                            label={isContinuousMode
                              ? `连续输注剂量 (${doseMassUnit})`
                              : isOralMode
                                ? `口服单次剂量 (${doseMassUnit})`
                                : isBolusMode
                                  ? `静脉注射剂量 (${doseMassUnit})`
                                  : `单次给药剂量 (${doseMassUnit})`}
                            rules={[{ required: true, message: '请输入给药剂量' }]}
                          >
                            <InputNumber
                              min={0.01}
                              step={1}
                              style={{ width: '100%' }}
                              placeholder="如 45"
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={4}>
                          <Form.Item
                            {...restField}
                            name={[name, 'dosing_interval']}
                            label={isContinuousMode ? '连续输注间隔 (h)' : '给药间隔 (h)'}
                            tooltip={isContinuousMode ? '用于记录当前连续输注执行间隔，便于重建历史方案；主结果仍按连续输注总量和泵速展示。' : undefined}
                            rules={[{ required: true, message: '请输入给药间隔' }]}
                          >
                            <InputNumber
                              min={4}
                              max={48}
                              step={1}
                              style={{ width: '100%' }}
                            />
                          </Form.Item>
                        </Col>
                        {!isContinuousMode && !hasZeroInputDuration && (
                          <Col xs={24} sm={12} md={4}>
                            <Form.Item
                              {...restField}
                              name={[name, 'infusion_duration']}
                              label="输注时间 (h)"
                              rules={[{ required: true, message: '请输入输注时间' }]}
                            >
                              <InputNumber
                                min={0.01}
                                step={0.25}
                                precision={2}
                                style={{ width: '100%' }}
                              />
                            </Form.Item>
                          </Col>
                        )}
                        <Col xs={24} sm={12} md={4}>
                          <Form.Item
                            {...restField}
                            name={[name, 'n_doses']}
                            label="给药次数"
                            tooltip={`根据开始给药时间、方案切换时间和采样时间自动计算。首剂记为 1 次，不足一个给药间隔不进位；采样落在下一次理论给药时间后 ${SAMPLING_BOUNDARY_TOLERANCE_MIN} 分钟内，默认仍按给药前采样处理。也可手动修改。`}
                          >
                            <InputNumber
                              min={1}
                              max={100}
                              step={1}
                              style={{ width: '100%' }}
                              placeholder="自动计算"
                            />
                          </Form.Item>
                          {autoRegimenDoses[name] != null && (
                            <Text type="secondary" style={{ fontSize: 11, marginTop: -8, display: 'block' }}>
                              <ClockCircleOutlined /> 自动: {autoRegimenDoses[name]} 次
                            </Text>
                          )}
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add({
                        infusion_duration: isContinuousMode
                          ? 24
                          : (hasZeroInputDuration ? 0 : drugConfig?.default_infusion_duration_h),
                        dosing_interval: isContinuousMode
                          ? drugConfig?.continuous_infusion?.default_bag_interval_h
                          : undefined,
                        n_doses_source: 'auto',
                      })}
                      block
                      icon={<PlusOutlined />}
                    >
                      添加给药方案
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            {/* ======== TDM Samples Section ======== */}
            <Divider orientation="left" plain>{isContinuousMode ? 'TDM 采样与稳态浓度数据' : 'TDM 血药浓度监测数据'}</Divider>
            <Form.List name="tdm_samples">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                      <Card
                        key={key}
                        size="small"
                        style={{ marginBottom: 12, background: '#fafafa' }}
                        title={<Text type="secondary">{`TDM采样 #${name + 1}`}</Text>}
                        extra={
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => remove(name)}
                            size="small"
                          >
                            删除
                          </Button>
                        }
                      >
                        <Row gutter={16}>
                          <Col xs={24} sm={12}>
                            <Form.Item
                              {...restField}
                              name={[name, 'sampling_time']}
                              label="采样时间"
                              rules={[{ required: true, message: '请选择采样时间' }]}
                            >
                              <DatePicker
                                showTime={{ format: 'HH:mm' }}
                                format="YYYY-MM-DD HH:mm"
                                style={{ width: '100%' }}
                                placeholder="选择采血日期时间"
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12}>
                            <Form.Item
                              {...restField}
                              name={[name, 'concentration']}
                              label={`实测血药浓度 (${concentrationUnit})`}
                              rules={[{ required: true, message: '请输入浓度' }]}
                            >
                              <InputNumber
                                min={0.0001}
                                step={0.1}
                                style={{ width: '100%' }}
                                placeholder="如 12.5"
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                    >
                      添加TDM采样记录
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            {!isContinuousMode && !hasZeroInputDuration && (
              <>
                <Divider orientation="left" plain>候选方案设置</Divider>
                <Card size="small" style={{ marginBottom: 16, background: '#fafcff' }}>
                  <Form.Item
                    name="future_infusion_duration_h"
                    label="输注时间 (h)"
                    tooltip="该值用于全部候选方案，并参与候选剂量优化、PK/PD 评价和药时曲线计算。"
                    rules={[
                      { required: true, message: '请输入输注时间' },
                      { type: 'number', min: 0.01, message: '输注时间必须大于 0' },
                      ...(shortestCandidateIntervalH != null
                        ? [{ type: 'number' as const, max: shortestCandidateIntervalH, message: `输注时间不得超过最短候选间隔 ${shortestCandidateIntervalH} h` }]
                        : []),
                    ]}
                    style={{ maxWidth: 360, marginBottom: 8 }}
                  >
                    <InputNumber
                      min={0.01}
                      max={shortestCandidateIntervalH}
                      step={0.25}
                      precision={2}
                      style={{ width: '100%' }}
                      placeholder="请输入候选方案统一采用的输注时间"
                    />
                  </Form.Item>
                  <Text type="secondary">
                    未修改时采用药物配置的默认值；提交后每个候选方案都会返回并展示实际采用的输注时间。
                  </Text>
                </Card>
              </>
            )}

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<ThunderboltOutlined />}
                size="large"
              >
                执行优化
              </Button>
            </Form.Item>
          </Form>

          {/* Optimization Results */}
          {result && displayResult && (
            <>
              <Divider />
              <Title level={5}>
                <SafetyOutlined /> 优化结果
              </Title>

              {/* 多方案选择表格 */}
              {regimens.length > 0 && (
                <Card title="候选给药方案（如需改为写入其他方案，可在此选择）" size="small" style={{ marginBottom: 16 }}>
                  <Alert
                    message={hasClinicallyMetRegimen
                      ? '请结合评价结果、疗效目标与安全边界，手动选择一个候选给药方案'
                      : '当前候选方案均超过最大剂量阈值，请谨慎评估'}
                    description="可直接修改每行剂量与输注时间；任一数值变化都会完整重算该行的 PK/PD、PTA、评价与药时曲线。"
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    banner
                  />
                  <GroupedCandidateRegimenTable
                    className="tdm-optimize__candidate-table"
                    rows={regimens}
                    columns={desktopCandidateColumns}
                    selectedIndex={selectedRegimen}
                    onSelect={handleCandidateSelect}
                    getEvaluation={(regimen, index) => resolveEvaluationStatus(
                      regimenStatuses[index],
                      regimen?.pta,
                    )}
                    isRecommended={(regimen) => regimen?.is_recommended === true}
                    formatDose={(dose) => formatResultDose(dose)}
                    isContinuous={isContinuousMode}
                    isMobile={isMobileShell}
                    renderMobileRow={renderMobileCandidateCard}
                    resetKey={result.calculation_id ?? submittedModelId}
                  />
                </Card>
              )}

              {isMobileShell && shouldShowTdmDetails && displayResult && (
                <Card
                  title={isContinuousMode
                    ? '已选方案详情（连续输注）'
                    : isOralMode
                      ? `已选口服方案详情（Q${displayResult.dosing_interval_h}h）`
                      : isBolusMode
                        ? `已选静脉注射方案详情（Q${displayResult.dosing_interval_h}h）`
                        : `已选方案详情（Q${displayResult.dosing_interval_h}h）`}
                  size="small"
                  className="tdm-optimize__detail-card"
                  style={{ marginBottom: 16 }}
                >
                  {activeRegimenStatus && (
                    <div className="tdm-optimize__detail-status">
                      <Tag color={resolveEvaluationStatus(activeRegimenStatus, (displayResult as any)?.pta).color} style={{ margin: 0, borderRadius: 999, paddingInline: 10 }}>
                        {resolveEvaluationStatus(activeRegimenStatus, (displayResult as any)?.pta).label}
                      </Tag>
                    </div>
                  )}
                  <div className="tdm-optimize__detail-grid">
                    {selectedPlanSummary.map((item) => (
                      <div key={item.label}>
                        <span className="tdm-optimize__detail-label">{item.label}</span>
                        <div className="tdm-optimize__detail-value">{item.value}</div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* 原给药方案 */}
              {result.original_regimen && (
                <Card title={isContinuousMode ? '原连续输注方案' : (isOralMode ? '原口服方案' : (isBolusMode ? '原静脉注射方案' : '原给药方案'))} size="small" style={{ marginBottom: 16 }}>
                  <Descriptions column={isMobileShell ? 1 : 3} size="small">
                    <Descriptions.Item label="剂量">
                      <Text strong style={{ fontSize: 16, color: '#8c8c8c' }}>
                        {formatResultDose(result.original_regimen.dose_mg)}
                        {result.original_regimen.dose_mg_kg != null && (
                          <Text type="secondary"> ({formatResultDosePerKg(result.original_regimen.dose_mg_kg)})</Text>
                        )}
                      </Text>
                    </Descriptions.Item>
	                    {isContinuousMode ? (
	                      <>
	                        <Descriptions.Item label="连续输注间隔">
	                          每 {result.original_regimen.interval_h} 小时
	                        </Descriptions.Item>
	                        <Descriptions.Item label="等效泵速">
	                          {result.original_regimen.interval_h > 0
                            ? `${formatResultDose(result.original_regimen.dose_mg / result.original_regimen.interval_h)}/h`
	                            : 'N/A'}
	                        </Descriptions.Item>
	                      </>
	                    ) : (
	                      <>
	                        <Descriptions.Item label="给药间隔">
	                          每 {result.original_regimen.interval_h} 小时
	                        </Descriptions.Item>
                        {!hasZeroInputDuration && (
	                          <Descriptions.Item label="输注时间">
	                            {result.original_regimen.infusion_h} 小时
	                          </Descriptions.Item>
	                        )}
	                      </>
	                    )}
                    {methodColumns.map((col: RegimenColumn) => {
                      const val = resolveMethodColumnValue(
                        result.original_regimen,
                        col,
                        result.original_regimen_pkpd,
                      );
                      const display = val != null
                        ? `${formatDisplayNumber(val, col.decimals ?? 2)}${col.format === 'percent' ? '%' : ''}`
                        : 'N/A';
                      return (
                        <Descriptions.Item key={col.dataKey} label={col.title}>
                          {display}
                        </Descriptions.Item>
                      );
                    })}
	                    {isContinuousMode ? (
	                      selectedMethod === 'Target Concentration' && !hasMethodField('predicted_css') && (
	                        <Descriptions.Item label="预计 Css">
	                          {formatConcentrationValue(result.original_regimen.predicted_css, 2)}
	                        </Descriptions.Item>
	                      )
	                    ) : (
	                      <>
	                        {!hasMethodField('predicted_cmin') && !hasMethodField('css_trough') && (
	                          <Descriptions.Item label="预测谷浓度 (Cmin)">
	                            {formatConcentrationValue(result.original_regimen.predicted_cmin, 2)}
	                          </Descriptions.Item>
	                        )}
	                        {!hasMethodField('predicted_cmax') && !hasMethodField('css_peak') && (
	                          <Descriptions.Item label="预测峰浓度 (Cmax)">
	                            {formatConcentrationValue(result.original_regimen.predicted_cmax, 2)}
	                          </Descriptions.Item>
	                        )}
	                      </>
	                    )}
                  </Descriptions>
                </Card>
              )}

              {shouldShowTdmDetails ? (
                <>
                  {hasOriginalNoAdjustment && (
                    <Alert
                      type="info"
                      showIcon
                      message="当前方案已满足当前评价规则，可供临床复核。"
                      description="下方展示当前方案的安全检查、PK/PD 分析、药时曲线和 AI 临床解读；是否维持当前方案或进一步调整，请结合临床情况由用户决定。"
                      style={{ marginBottom: 16 }}
                    />
                  )}
                  <Row gutter={[16, 16]} className="tdm-optimize__result-grid">
                    {!isMobileShell && (
                      <Col xs={24} md={12}>
                        <Card
                          title={hasOriginalNoAdjustment
                            ? (isContinuousMode ? '当前连续输注方案' : (isOralMode ? '当前口服方案' : (isBolusMode ? '当前静脉注射方案' : '当前给药方案')))
                            : (isContinuousMode ? '已选连续输注方案' : (isOralMode ? '已选口服方案' : (isBolusMode ? '已选静脉注射方案' : '已选给药方案')))}
                          size="small"
                          className="tdm-optimize__result-card tdm-optimize__clinical-card"
                          extra={activeRegimenStatus ? (
                            <Tag color={resolveEvaluationStatus(activeRegimenStatus, (displayResult as any)?.pta).color} style={{ margin: 0 }}>
                              {resolveEvaluationStatus(activeRegimenStatus, (displayResult as any)?.pta).label}
                            </Tag>
                          ) : undefined}
                        >
                          <Descriptions column={1} size="small">
                            {isContinuousMode ? (
                              <>
                                <Descriptions.Item label="维持总日剂量">
                                  <Text strong style={{ fontSize: 18, color: '#0B2545' }}>
                                    {formatResultDose(displayResult.maintenance_daily_dose_mg ?? displayResult.recommended_dose_mg)}/24h
                                  </Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="泵速">
                                  {displayResult.maintenance_rate_mg_h != null ? `${formatResultDose(displayResult.maintenance_rate_mg_h)}/h` : 'N/A'}
                                </Descriptions.Item>
                                {continuousPrimaryMetric?.dataKey === 'predicted_css' && (
                                  <Descriptions.Item label={continuousPrimaryMetric.label}>
                                    {formatConcentrationValue(displayResult.predicted_css, continuousPrimaryMetric.decimals)}
                                  </Descriptions.Item>
                                )}
	                                {displayResult.bag_interval_h != null && (
	                                  <Descriptions.Item label="连续输注间隔">
	                                    每 {displayResult.bag_interval_h} 小时
	                                  </Descriptions.Item>
	                                )}
	                                {displayResult.recommended_loading_dose_mg != null && (
                                  <Descriptions.Item label="负荷剂量">
                                    {formatResultDose(displayResult.recommended_loading_dose_mg)}
                                  </Descriptions.Item>
                                )}
                              </>
                            ) : (
                              <>
                                <Descriptions.Item label="所选剂量">
                                  <Text strong style={{ fontSize: 18, color: '#0B2545' }}>
                                    {formatResultDose(displayResult.recommended_dose_mg)}
                                    {displayResult.recommended_dose_mg_kg != null && (
                                      <Text type="secondary"> ({formatResultDosePerKg(displayResult.recommended_dose_mg_kg)})</Text>
                                    )}
                                  </Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="给药间隔">
                                  每 {displayResult.dosing_interval_h} 小时
                                </Descriptions.Item>
                                {!hasZeroInputDuration && (
                                  <Descriptions.Item label="输注时间">
                                    {displayResult.infusion_duration_h} 小时
                                  </Descriptions.Item>
                                )}
                              </>
                            )}
                            {methodColumns.map((col: RegimenColumn) => {
                              const val = resolveMethodColumnValue(
                                displayResult,
                                col,
                                displayResult.pkpd_results,
                              );
                              const display = val != null
                                ? `${formatDisplayNumber(val, col.decimals ?? 2)}${col.format === 'percent' ? '%' : ''}`
                                : 'N/A';
                              return (
                                <Descriptions.Item key={col.dataKey} label={col.title}>
                                  {display}
                                </Descriptions.Item>
                              );
                            })}
	                            {isContinuousMode ? (
                                selectedMethod === 'Target Concentration' && !hasMethodField('predicted_css') && (
                                  <Descriptions.Item label="预计 Css">
                                    {formatConcentrationValue(displayResult.predicted_css, 2)}
                                  </Descriptions.Item>
                                )
                              ) : (
	                              <>
	                                {!hasMethodField('predicted_cmin') && !hasMethodField('css_trough') && (
	                                  <Descriptions.Item label="预测谷浓度 (Cmin)">
	                                    {formatConcentrationValue(displayResult.predicted_cmin, 2)}
	                                  </Descriptions.Item>
	                                )}
	                                {!hasMethodField('predicted_cmax') && !hasMethodField('css_peak') && (
	                                  <Descriptions.Item label="预测峰浓度 (Cmax)">
	                                    {formatConcentrationValue(displayResult.predicted_cmax, 2)}
	                                  </Descriptions.Item>
	                                )}
	                              </>
	                            )}
                          </Descriptions>
                        </Card>
                      </Col>
                    )}

                    <Col xs={24} md={isMobileShell ? 24 : 12}>
                      <div className="tdm-optimize__clinical-stack">
                        <Card title="安全检查" size="small" className="tdm-optimize__clinical-card tdm-optimize__safety-card">
                          <div className={`tdm-optimize__safety-status ${!safety.safe ? 'tdm-optimize__safety-status--danger' : safety.warnings?.length ? 'tdm-optimize__safety-status--warning' : 'tdm-optimize__safety-status--ok'}`}>
                            {safety.safe ? (
                              <Tag color="green" icon={<SafetyOutlined />}>安全检查通过</Tag>
                            ) : (
                              <Tag color="red">存在安全风险</Tag>
                            )}
                            <span className="tdm-optimize__safety-copy">
                              {safety.safe && (!safety.warnings || safety.warnings.length === 0) && (!safety.errors || safety.errors.length === 0)
                                ? '安全边界内'
                                : `${(safety.warnings?.length || 0) + (safety.errors?.length || 0)} 项需复核`}
                            </span>
                          </div>
                          {((safety.warnings?.length || 0) > 0 || (safety.errors?.length || 0) > 0) && (
                            <div className="tdm-optimize__safety-alerts">
                              {safety.warnings?.map((w: string, i: number) => (
                                <Alert key={`w-${i}`} type="warning" message={w} banner />
                              ))}
                              {safety.errors?.map((e: string, i: number) => (
                                <Alert key={`e-${i}`} type="error" message={e} banner />
                              ))}
                            </div>
                          )}
                        </Card>

                      {hasDisplayablePkParameters(displayResult.pk_parameters) && (
                        <Card title="个体化 PK 参数 (CL, V)" size="small" className="tdm-optimize__clinical-card tdm-optimize__pk-card">
                          <Descriptions
                            column={isMobileShell ? 1 : 2}
                            size="small"
                            styles={{
                              label: { minWidth: 132, whiteSpace: 'normal' },
                              content: { minWidth: 96 },
                            }}
                          >
                            {Object.entries(displayResult.pk_parameters).filter(
                              ([key, val]) => PK_PARAMETER_KEYS.has(key) && normalizeFiniteNumber(val) !== null,
                            ).map(([key, val]) => {
                              let label = key;
                              let unit = '';
                              if (['CL', 'Cl', 'cl', 'clearance', 'clearance_l_h', 'CL_individual'].includes(key)) { label = '清除率 (CL)'; unit = ' L/h'; }
                              else if (key === 'CL_pop') { label = '群体 CL'; unit = ' L/h'; }
                              else if (['Vc', 'VC', 'vc', 'V', 'V1', 'Vd', 'vd', 'volume', 'Vc_individual'].includes(key)) { label = '分布容积 (Vc)'; unit = ' L'; }
                              else if (key === 'Vc_pop') { label = '群体 Vc'; unit = ' L'; }
                              else if (['ke', 'Ke', 'kel', 'KEL'].includes(key)) { label = '消除速率常数 (ke)'; unit = ' h⁻¹'; }
                              else if (key === 'half_life') { label = '半衰期 (t½)'; unit = ' h'; }
                              return (
                                <Descriptions.Item key={key} label={label}>
                                  {typeof val === 'number' ? formatDisplayNumber(val, 2) : String(val)}{unit}
                                </Descriptions.Item>
                              );
                            })}
                            {displayResult.steady_state_info && (
                              <Descriptions.Item label="达稳态时间">
                                约 {displayResult.steady_state_info.n_doses} 个剂量（~{displayResult.steady_state_info.time_h} 小时）
                              </Descriptions.Item>
                            )}
                          </Descriptions>
                        </Card>
                      )}
                      </div>
                    </Col>
                  </Row>

                  {selectedRegimen !== null
                    && regimenRecalcStatus[selectedRegimen] === 'loading'
                    && !combinedCurveData && (
                    <Alert
                      type="info"
                      showIcon
                      message="正在按当前给药历史与所选候选间隔生成模型药时曲线"
                      style={{ marginTop: 16 }}
                    />
                  )}
                  {currentRegimenPredictionUnavailable && (
                    <Alert
                      type="warning"
                      showIcon
                      message="当前方案后验预测区间暂不可用"
                      description="系统不会使用候选方案曲线或浏览器公式曲线替代当前方案。请检查模型蒙特卡洛配置或 R 模拟结果。"
                      style={{ marginTop: 16 }}
                    />
                  )}
                  {(displaySimulationData || combinedCurveData) && (
                    <Card title="药时曲线" style={{ marginTop: 16 }}>
                      <PKCurve
                        key={`pk-${displayResult.dosing_interval_h}-${displayResult.recommended_dose_mg}`}
                        simulationData={displaySimulationData ?? undefined}
                        combinedCurveData={combinedCurveData}
                        targetRange={(() => {
                          const parameterMap: Record<string, string> = {
                            'Target Concentration': isContinuousMode ? continuousTargetParameter : 'Cmin',
                            'Target Cmax': 'Cmax',
                            'Target AUC': 'AUC24',
                            'AUC/MIC': 'AUC24/MIC',
                            '%fT>MIC': '%fT>MIC',
                          };
                          const target = findTarget(selectedMethod, parameterMap[selectedMethod]);
                          if (!target) return {};
                          const parameter = (target.parameter || '').toLowerCase();
                          if (parameter.includes('auc') || parameter.includes('ft>') || parameter.includes('ft >')) return {};
                          return { troughMin: target.min, troughMax: target.max };
                        })()}
                        mic={convertCanonicalValue(
                          displayResult.pkpd_results?.['%fT>MIC']?.details?.default_mic
                            ?? result.pkpd_results?.['%fT>MIC']?.details?.default_mic,
                          micUnit,
                          'concentration',
                        ) ?? undefined}
                        nephrotoxicityCmin={getExposureSafetyLimit(drugConfig, 'exposure.cmin', concentrationUnit, {
                          selectedMethod,
                          administrationMode: tdmAdministrationMode,
                          covariates,
                        })}
                        tdmPoints={tdmPoints}
                        concentrationUnit={concentrationUnit}
                        micUnit={micUnit}
                      />
                    </Card>
                  )}

              {/* PK/PD 分析 */}
              {displayResult.pkpd_results && Object.keys(displayResult.pkpd_results).length > 0 && (
                <PkPdResultsCard
                  pkpdResults={displayResult.pkpd_results}
                  preferredMethod={selectedMethod}
                  evaluationRegimen={displayResult}
                  drugConfig={drugConfig}
                  administrationMode={tdmAdministrationMode}
                />
              )}

              {/* Clinical interpretation */}
              {(displayInterpretation || interpretLoading) && (
                <ClinicalInterpretation
                  interpretation={editedInterpretation ?? displayInterpretation}
                  regimenChanged={selectedRegimen !== null}
                  activeRegimen={displayResult ? {
                    dose: displayResult.recommended_dose_mg,
                    interval: displayResult.dosing_interval_h,
                    infusionH: displayResult.infusion_duration_h,
                  } : undefined}
                  loading={interpretLoading}
                  onInterpretationChange={setEditedInterpretation}
                />
              )}

              {result._note && (
                <Alert
                  type="info"
                  message={result._note}
                  style={{ marginTop: 16 }}
                  showIcon
                />
              )}
              {result.optimization_method && (
                <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                  优化方法: {formatOptimizationMethodLabel(result.optimization_method)}
                </Text>
              )}

                  {/* 生成报告按钮 */}
                  {result.calculation_id && (
                    isMobileShell ? (
                      <>
                        <div className="tdm-optimize__sticky-spacer" />
                        <div className="tdm-optimize__sticky-cta">
                          <Button
                            block
                            type="primary"
                            icon={<FileTextOutlined />}
                            size="large"
                            onClick={handleGenerateReport}
                          >
                            生成报告
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div style={{ marginTop: 16, textAlign: 'center' }}>
                        <Button
                          type="primary"
                          icon={<FileTextOutlined />}
                          size="large"
                          onClick={handleGenerateReport}
                        >
                          生成报告
                        </Button>
                      </div>
                    )
                  )}
                </>
              ) : (
                <Alert
                  message="请选择一个候选给药方案后查看个体化结果、曲线和临床解读。"
                  type="info"
                  showIcon
                />
              )}
            </>
          )}
        </>
      )}
      </Card>
    </>
  );
};

export default TDMOptimizePanel;
